import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  FlatList,
  Dimensions,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { launchImageLibraryAsync } from 'expo-image-picker';
import { getDocumentAsync } from 'expo-document-picker';
// DateTimePicker removed - using web-compatible date/time inputs
import { Ionicons } from '@expo/vector-icons';
import { Input, Button, Toggle } from '../design-system';
import { RichTextEditor } from '../design-system/RichTextEditor';
import { BusinessUserPicker } from '../BusinessUserPicker';
import { MapLocationPicker } from '../MapLocationPicker';
import { VoiceRecorder } from '../VoiceRecorder';
import { MediaService, MediaResult } from '../../services/media.service';
import type { BusinessUser } from '../../services/api';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { apiService } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { getCurrentApiConfig } from '../../config/api.config';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export type QuickReplyItemType =
  | 'fixed_message'
  | 'data_set'
  | 'product'
  | 'catalog'
  | 'webhook'
  | 'url'
  | 'task'
  | 'calendar'
  | 'assign_user'
  | 'assign_department'
  | 'flow';

export interface QuickReplyItemFormData {
  displayText: string;
  type: QuickReplyItemType;
  config: any;
  displayOrder?: number;
  isActive?: boolean;
  icon?: string;
  bodyText?: string; // Body text used when item triggers a nested set
  setIds?: string[]; // Array of set IDs this item belongs to
}

export interface QuickReplyItemFormProps {
  initialData?: Partial<QuickReplyItemFormData> & { _id?: string }; // Include _id for item identification
  onSubmit: (data: QuickReplyItemFormData) => void;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
  availableRepliesSets?: Array<{ _id: string; name: string }>; // For data_set type
  hideActions?: boolean;
  submitRef?: React.MutableRefObject<(() => void) | null>;
}

const ITEM_TYPE_OPTIONS: Array<{ value: QuickReplyItemType; label: string; icon: string; description: string; disabled?: boolean }> = [
  // Most commonly used - basic messaging
  { value: 'fixed_message', label: 'Fixed Message', icon: 'chatbubble-outline', description: 'Send a predefined message (text, media, voice, location)' },
  { value: 'data_set', label: 'Nested Replies Set', icon: 'layers-outline', description: 'Display another replies set' },
  { value: 'flow', label: 'Flow', icon: 'git-pull-request-outline', description: 'Start a flow when tapped' },

  // E-commerce features
  { value: 'product', label: 'Product', icon: 'cube-outline', description: 'Show product information' },
  { value: 'catalog', label: 'Catalog', icon: 'grid-outline', description: 'Show catalog information' },
  
  // External links
  { value: 'url', label: 'URL', icon: 'link-outline', description: 'Open a URL link' },
  
  // Assignment features
  { value: 'assign_user', label: 'Assign to User', icon: 'person-outline', description: 'Assign conversation to user' },
  { value: 'assign_department', label: 'Assign to Department', icon: 'business-outline', description: 'Assign conversation to department' },
  
  // Calendar/booking
  { value: 'calendar', label: 'Calendar Event', icon: 'calendar-outline', description: 'Create calendar event' },
  
  // Disabled options at the end
  { value: 'webhook', label: 'Webhook', icon: 'code-working-outline', description: 'Trigger a predefined Developer Access webhook' },
  { value: 'task', label: 'Create Task', icon: 'checkmark-circle-outline', description: 'Create a task', disabled: true },
];

const WEBHOOK_CHANNEL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  'facebook-messenger': 'Facebook Messenger',
};

export const QuickReplyItemForm: React.FC<QuickReplyItemFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Save Item',
  loading = false,
  availableRepliesSets = [],
  hideActions = false,
  submitRef,
}) => {
  const styles = useThemedStyles(getStyles);
  const [formData, setFormData] = useState<QuickReplyItemFormData>({
    displayText: initialData?.displayText || '',
    type: initialData?.type || 'fixed_message',
    config: initialData?.config || {},
    displayOrder: initialData?.displayOrder,
    isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
    icon: initialData?.icon,
    bodyText: initialData?.bodyText || '',
  });

  // Helper function to convert Firebase Storage URL to proxy URL
  const convertToProxyUrl = (firebaseUrl: string, mimeType: string): string => {
    try {
      // If URL is already a proxy URL, return as-is
      if (firebaseUrl.includes('/api/storage/media/proxy')) {
        return firebaseUrl;
      }
      
      // Reject blob URLs - they must be uploaded first
      if (firebaseUrl.startsWith('blob:')) {
        console.warn('Blob URL detected - should not be saved. URL must be uploaded to Firebase Storage first.');
        return firebaseUrl; // Return as-is, will be rejected by backend validation
      }
      
      // If URL is a signed URL (contains GoogleAccessId, Expires, Signature), extract the file path
      // and construct a non-signed Firebase Storage URL
      let urlToProxy = firebaseUrl;
      if (firebaseUrl.includes('GoogleAccessId=') || firebaseUrl.includes('Expires=') || firebaseUrl.includes('Signature=')) {
        try {
          // Parse the signed URL to extract the file path
          const urlObj = new URL(firebaseUrl);
          if (urlObj.hostname === 'storage.googleapis.com') {
            // Direct storage format: https://storage.googleapis.com/{bucket}/{path}?query
            const pathParts = urlObj.pathname.split('/').filter(p => p);
            if (pathParts.length >= 2) {
              const bucket = pathParts[0];
              const filePath = pathParts.slice(1).join('/');
              // Construct non-signed Firebase Storage API URL
              const encodedPath = encodeURIComponent(filePath);
              urlToProxy = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
              console.log(`[QuickReplyItemForm] Converted signed URL to non-signed: ${urlToProxy.substring(0, 150)}`);
            }
          } else if (urlObj.hostname === 'firebasestorage.googleapis.com') {
            // Already in API format, just remove query parameters
            const pathMatch = urlObj.pathname.match(/\/v0\/b\/([^/]+)\/o\/(.+)/);
            if (pathMatch) {
              const bucket = pathMatch[1];
              const encodedPath = pathMatch[2];
              urlToProxy = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
              console.log(`[QuickReplyItemForm] Cleaned signed URL to non-signed: ${urlToProxy.substring(0, 150)}`);
            }
          }
        } catch (parseError) {
          console.warn('Failed to parse signed URL, using original:', parseError);
          // Continue with original URL
        }
      }
      
      const apiConfig = getCurrentApiConfig();
      const apiBaseUrl = apiConfig.baseURL || 'https://api.connect.dislix.com';
      const encodedUrl = encodeURIComponent(urlToProxy);
      const encodedMimeType = encodeURIComponent(mimeType);
      return `${apiBaseUrl}/api/storage/media/proxy?url=${encodedUrl}&mimeType=${encodedMimeType}`;
    } catch (error) {
      console.error('Error converting to proxy URL:', error);
      // Fallback to original URL if conversion fails
      return firebaseUrl;
    }
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [showSetsSelector, setShowSetsSelector] = useState(false);
  const [showCatalogSelector, setShowCatalogSelector] = useState(false);
  const [showRepliesSetSelector, setShowRepliesSetSelector] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showCalendarSelector, setShowCalendarSelector] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [showDepartmentSelector, setShowDepartmentSelector] = useState(false);
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [showFlowSelector, setShowFlowSelector] = useState(false);
  const [setsSearchQuery, setSetsSearchQuery] = useState('');
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [repliesSetSearchQuery, setRepliesSetSearchQuery] = useState('');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [calendarSearchQuery, setCalendarSearchQuery] = useState('');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [selectedSetIds, setSelectedSetIds] = useState<string[]>(initialData?.setIds || []);
  const [allAvailableSets, setAllAvailableSets] = useState<Array<{ _id: string; name: string }>>(availableRepliesSets || []);
  const [catalogues, setCatalogues] = useState<Array<{ _id: string; name: string }>>([]);
  const [products, setProducts] = useState<Array<{ _id: string; name: string; catalogueId?: string; catalogueName?: string }>>([]);
  const [calendars, setCalendars] = useState<Array<{ _id: string; name: string }>>([]);
  const [availableFlows, setAvailableFlows] = useState<Array<{ _id: string; name: string }>>([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<BusinessUser[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<Array<{ _id: string; name: string; description?: string; managerId?: any }>>([]);
  const [availableItems, setAvailableItems] = useState<Array<{ _id: string; displayText: string; type: string; icon?: string }>>([]);
  const [selectedItems, setSelectedItems] = useState<Array<{ _id: string; displayText: string; type: string; icon?: string }>>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [availableWebhooks, setAvailableWebhooks] = useState<Array<{ _id: string; title: string; channel: string; webhookUrl: string; isActive?: boolean }>>([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  // Generate temp ID once per form session for consistent folder naming
  const [tempItemId] = useState<string>(() => initialData?._id || `temp_${Date.now()}`);
  const [loadingCatalogues, setLoadingCatalogues] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingSets, setLoadingSets] = useState(false);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  
  const { currentBusiness } = useAppStore();
  
  // Date picker states for calendar type
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  useEffect(() => {
    if (initialData) {
      const initialSetIds = initialData.setIds || [];
      setFormData({
        displayText: initialData.displayText || '',
        type: initialData.type || 'fixed_message',
        config: initialData.config || {},
        displayOrder: initialData.displayOrder,
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
        icon: initialData.icon,
        // Was missing here (present only in the initial useState above) — since this
        // is a full object replacement rather than a merge, omitting it silently reset
        // bodyText to undefined every time this effect re-ran (e.g. a parent re-render
        // passing a new initialData object reference), which visually looked like the
        // Body Text field for a data_set item "just disappearing"/clearing itself.
        bodyText: initialData.bodyText || '',
        setIds: initialSetIds,
      });
      setSelectedSetIds(initialSetIds);

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Load catalogues when catalog type is selected
  useEffect(() => {
    if (formData.type === 'catalog') {
      loadCatalogues();
    }
  }, [formData.type]);

  // Load products when product type is selected
  useEffect(() => {
    if (formData.type === 'product') {
      loadProducts();
    }
  }, [formData.type]);

  // Load all sets when data_set type is selected
  useEffect(() => {
    if (formData.type === 'data_set') {
      loadAllSets();
    }
  }, [formData.type]);

  // Load calendars when calendar type is selected
  useEffect(() => {
    if (formData.type === 'calendar') {
      loadCalendars();
    }
  }, [formData.type]);

  // Load active flows when flow type is selected
  useEffect(() => {
    if (formData.type === 'flow') {
      loadFlows();
    }
  }, [formData.type]);

  // Load available items when fixed_message type is selected
  useEffect(() => {
    if (formData.type === 'fixed_message') {
      loadAvailableItems();
    }
  }, [formData.type]);

  // Load developer access webhooks when webhook type is selected
  useEffect(() => {
    if (formData.type === 'webhook') {
      loadDeveloperWebhooks();
    }
  }, [formData.type]);

  // Load selected items details when availableItems are loaded or config changes
  useEffect(() => {
    if (formData.type === 'fixed_message' && availableItems.length > 0) {
      const selectedItemIds = formData.config?.fixedMessage?.selectedItems || [];
      if (selectedItemIds.length > 0) {
        loadSelectedItemsDetails(selectedItemIds);
      } else {
        setSelectedItems([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.config?.fixedMessage?.selectedItems, availableItems.length, formData.type]);

  // Load users details function - similar to department screen (for multiple users)
  const loadUsersDetails = useCallback(async (userIds: string[]) => {
    if (!currentBusiness?._id || !userIds || userIds.length === 0) {
      setSelectedUsers([]);
      return;
    }
    
    try {
      const response = await apiService.getBusinessUsers(currentBusiness._id);
      if (response.statusCode === 1000 && response.data) {
        const loadedUsers: BusinessUser[] = [];
        
        for (const userId of userIds) {
          const userIdStr = String(userId).trim();
          
          const user = response.data.find((bu: BusinessUser) => {
            let buUserIdStr = '';
            if (typeof bu.userId === 'string') {
              buUserIdStr = bu.userId.trim();
            } else if (bu.userId && typeof bu.userId === 'object' && '_id' in bu.userId) {
              buUserIdStr = String((bu.userId as any)._id || '').trim();
            } else {
              buUserIdStr = String(bu.userId || '').trim();
            }
            
            const userObjectId = bu.user?._id ? String(bu.user._id).trim() : '';
            return buUserIdStr === userIdStr || userObjectId === userIdStr;
          });
          
          if (user) {
            loadedUsers.push(user);
          }
        }
        
        setSelectedUsers(loadedUsers);
      } else {
        setSelectedUsers([]);
      }
    } catch (error) {
      console.error('Error loading users details:', error);
      setSelectedUsers([]);
    }
  }, [currentBusiness?._id]);

  // Load users details when assign_user type is selected and userIds exist
  useEffect(() => {
    if (formData.type === 'assign_user' && currentBusiness?._id) {
      const userIds = formData.config?.assignUser?.userIds || 
                     (formData.config?.assignUser?.userId ? [formData.config.assignUser.userId] : []);
      if (userIds.length > 0) {
        loadUsersDetails(userIds);
      } else {
        setSelectedUsers([]);
      }
    } else if (formData.type !== 'assign_user') {
      setSelectedUsers([]);
    }
  }, [formData.type, formData.config?.assignUser?.userIds, formData.config?.assignUser?.userId, currentBusiness?._id, loadUsersDetails]);

  // Load departments details function - similar to loadUsersDetails
  const loadDepartmentsDetails = useCallback(async (departmentIds: string[]) => {
    if (!currentBusiness?._id || !departmentIds || departmentIds.length === 0) {
      setSelectedDepartments([]);
      return;
    }
    
    try {
      const response = await apiService.getDepartments(currentBusiness._id);
      if (response.statusCode === 1000 && response.data) {
        const loadedDepartments: Array<{ _id: string; name: string; description?: string; managerId?: any }> = [];
        
        for (const departmentId of departmentIds) {
          const departmentIdStr = String(departmentId).trim();
          
          const department = response.data.find((dept: any) => {
            const deptIdStr = String(dept._id || '').trim();
            return deptIdStr === departmentIdStr;
          });
          
          if (department) {
            loadedDepartments.push({
              _id: department._id,
              name: department.name || 'Unknown Department',
              description: department.description,
              managerId: department.managerId,
            });
          }
        }
        
        setSelectedDepartments(loadedDepartments);
      } else {
        setSelectedDepartments([]);
      }
    } catch (error) {
      console.error('Error loading departments details:', error);
      setSelectedDepartments([]);
    }
  }, [currentBusiness?._id]);

  // Load departments details when assign_department type is selected and departmentIds exist
  useEffect(() => {
    if (formData.type === 'assign_department' && currentBusiness?._id) {
      const departmentIds = formData.config?.assignDepartment?.departmentIds || 
                           (formData.config?.assignDepartment?.departmentId ? [formData.config.assignDepartment.departmentId] : []);
      if (departmentIds.length > 0) {
        loadDepartmentsDetails(departmentIds);
      } else {
        setSelectedDepartments([]);
      }
    } else if (formData.type !== 'assign_department') {
      setSelectedDepartments([]);
    }
  }, [formData.type, formData.config?.assignDepartment?.departmentIds, formData.config?.assignDepartment?.departmentId, currentBusiness?._id, loadDepartmentsDetails]);

  // Initialize default dates when calendar type is selected
  useEffect(() => {
    if (formData.type === 'calendar' && !formData.config?.calendar?.startDate) {
      const now = new Date();
      const defaultStartDate = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
      const defaultEndDate = new Date(defaultStartDate.getTime() + 60 * 60 * 1000); // 1 hour later
      
      setFormData((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          calendar: {
            ...(prev.config?.calendar || {}),
            startDate: defaultStartDate.toISOString(),
            endDate: defaultEndDate.toISOString(),
          },
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type]);

  // Update allAvailableSets when availableRepliesSets prop changes
  useEffect(() => {
    if (availableRepliesSets && availableRepliesSets.length > 0) {
      setAllAvailableSets(availableRepliesSets);
    }
  }, [availableRepliesSets]);

  const loadCatalogues = async () => {
    try {
      setLoadingCatalogues(true);
      const response = await apiService.getCatalogues();
      if (response.statusCode === 1000 && response.data?.catalogues) {
        setCatalogues(response.data.catalogues);
      }
    } catch (error) {
      console.error('Error loading catalogues:', error);
    } finally {
      setLoadingCatalogues(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      // Load all catalogues first
      const cataloguesResponse = await apiService.getCatalogues();
      if (cataloguesResponse.statusCode === 1000 && cataloguesResponse.data?.catalogues) {
        const allProducts: Array<{ _id: string; name: string; catalogueId?: string; catalogueName?: string }> = [];
        
        // Fetch products from each catalogue
        for (const catalogue of cataloguesResponse.data.catalogues) {
          try {
            const productsResponse = await apiService.getProductsByCatalogue(catalogue._id, {
              limit: 1000, // Load all products from each catalogue
              skip: 0,
            });
            
            if (productsResponse.statusCode === 1000 && productsResponse.data?.products) {
              // Add catalogue info to each product for display
              const productsWithCatalogue = productsResponse.data.products.map((product: any) => ({
                _id: product._id,
                name: product.name || product.title || 'Unnamed Product',
                catalogueId: catalogue._id,
                catalogueName: catalogue.name,
              }));
              allProducts.push(...productsWithCatalogue);
            }
          } catch (error) {
            console.error(`Error loading products from catalogue ${catalogue._id}:`, error);
            // Continue with other catalogues even if one fails
          }
        }
        
        setProducts(allProducts);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadAllSets = async () => {
    try {
      setLoadingSets(true);
      const response = await apiService.getQuickReplySets({
        limit: 1000, // Load all sets
        skip: 0,
      });
      if (response.statusCode === 1000 && response.data?.quickReplySets) {
        const sets = response.data.quickReplySets.map((set: any) => ({
          _id: set._id,
          name: set.name,
        }));
        setAllAvailableSets(sets);
      }
    } catch (error) {
      console.error('Error loading sets:', error);
    } finally {
      setLoadingSets(false);
    }
  };

  const loadFlows = async () => {
    try {
      setLoadingFlows(true);
      const response = await apiService.getFlows({ isActive: true, limit: 1000, skip: 0 });
      const arr: any[] = Array.isArray(response.data) ? response.data : (response.data as any)?.items || [];
      setAvailableFlows(arr.map((f: any) => ({ _id: f._id, name: f.name || 'Unnamed Flow' })));
    } catch (error) {
      console.error('Error loading flows:', error);
    } finally {
      setLoadingFlows(false);
    }
  };

  const loadCalendars = async () => {
    try {
      setLoadingCalendars(true);
      console.log('[QuickReplyItemForm] Loading calendars...');
      
      // The API will automatically use the authenticated user's businessId
      const response = await apiService.getCalendars({
        isActive: true
      });
      
      console.log('[QuickReplyItemForm] Calendar API response:', {
        statusCode: response.statusCode,
        hasData: !!response.data,
        dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
        errors: response.errors,
        messages: response.messages,
        fullResponse: response
      });
      
      if (response.statusCode === 1000) {
        // Check if data is an array directly or nested
        let calendarsArray: any[] = [];
        
        if (Array.isArray(response.data)) {
          calendarsArray = response.data;
        } else if (response.data && Array.isArray(response.data.calendars)) {
          calendarsArray = response.data.calendars;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          calendarsArray = response.data.data;
        }
        
        console.log('[QuickReplyItemForm] Parsed calendars array:', calendarsArray.length, 'calendars');
        
        const calendarsList = calendarsArray.map((calendar: any) => ({
          _id: calendar._id,
          name: calendar.name || 'Unnamed Calendar',
        }));
        
        console.log('[QuickReplyItemForm] Setting calendars:', calendarsList.length, 'calendars');
        setCalendars(calendarsList);
      } else {
        console.warn('[QuickReplyItemForm] Failed to load calendars:', response.errors || response.messages || 'Unknown error');
        console.log('[QuickReplyItemForm] Full response:', JSON.stringify(response, null, 2));
      }
    } catch (error) {
      console.error('[QuickReplyItemForm] Error loading calendars:', error);
    } finally {
      setLoadingCalendars(false);
    }
  };

  const loadAvailableItems = async () => {
    try {
      setLoadingItems(true);
      const response = await apiService.getQuickReplyItems({
        limit: 1000,
        skip: 0,
      });
      if (response.statusCode === 1000 && response.data?.items) {
        // Filter out the current item if editing
        const currentItemId = initialData?._id;
        const filtered = response.data.items
          .filter((item: any) => item._id !== currentItemId && item.isActive)
          .map((item: any) => ({
            _id: item._id,
            displayText: item.displayText || 'Unnamed Item',
            type: item.type || 'fixed_message',
            icon: item.icon,
          }));
        setAvailableItems(filtered);
      }
    } catch (error) {
      console.error('Error loading available items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const loadDeveloperWebhooks = async () => {
    try {
      setLoadingWebhooks(true);
      const response = await apiService.listWebhooks();
      if (response.statusCode === 1000 && Array.isArray(response.data)) {
        const activeWebhooks = response.data
          .filter((webhook: any) => webhook?.status === 1 || webhook?.isActive === true || webhook?.isActive === undefined)
          .map((webhook: any) => ({
            _id: String(webhook._id || '').trim(),
            title: String(webhook.title || 'Untitled Webhook').trim(),
            channel: String(webhook.channel || '').trim(),
            webhookUrl: String(webhook.webhookUrl || ''),
            isActive: webhook.isActive,
          }))
          .filter((webhook: { _id: string; channel: string }) => !!webhook._id && !!webhook.channel);

        setAvailableWebhooks(activeWebhooks);
      } else {
        setAvailableWebhooks([]);
      }
    } catch (error) {
      console.error('Error loading developer access webhooks:', error);
      setAvailableWebhooks([]);
    } finally {
      setLoadingWebhooks(false);
    }
  };

  const loadSelectedItemsDetails = async (itemIds: string[]) => {
    if (!itemIds || itemIds.length === 0) {
      setSelectedItems([]);
      return;
    }
    
    try {
      const itemsDetails: Array<{ _id: string; displayText: string; type: string; icon?: string }> = [];
      
      // First try to find items from availableItems
      for (const itemId of itemIds) {
        const foundItem = availableItems.find(item => item._id === itemId);
        if (foundItem) {
          itemsDetails.push(foundItem);
        } else {
          // If not found in availableItems, fetch from API
          try {
            const response = await apiService.getQuickReplyItemById(itemId);
            if (response.statusCode === 1000 && response.data) {
              itemsDetails.push({
                _id: response.data._id,
                displayText: response.data.displayText || 'Unnamed Item',
                type: response.data.type || 'fixed_message',
                icon: response.data.icon,
              });
            }
          } catch (error) {
            console.error(`Error loading item ${itemId}:`, error);
          }
        }
      }
      
      setSelectedItems(itemsDetails);
    } catch (error) {
      console.error('Error loading selected items details:', error);
      setSelectedItems([]);
    }
  };


  // URL validation helper
  const isValidUrl = (urlString: string): boolean => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Date formatting helper
  const formatDateTime = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.displayText.trim()) {
      newErrors.displayText = 'Display text is required';
    }

    // Type-specific validation
    switch (formData.type) {
      case 'fixed_message':
        if (!formData.config?.fixedMessage?.messageType) {
          newErrors['config.fixedMessage.messageType'] = 'Message type is required';
        } else {
          const msgType = formData.config.fixedMessage.messageType;
          if (msgType === 'text' && (!formData.config.fixedMessage.text || !formData.config.fixedMessage.text.trim())) {
            newErrors['config.fixedMessage.text'] = 'Text is required for text message type';
          }
          if ((msgType === 'image' || msgType === 'video' || msgType === 'audio' || msgType === 'document')) {
            if (!formData.config.fixedMessage.mediaUrl) {
              newErrors['config.fixedMessage.mediaUrl'] = 'Media file is required for this message type';
            } else if (formData.config.fixedMessage.mediaUrl.startsWith('blob:')) {
              newErrors['config.fixedMessage.mediaUrl'] = 'Media file must be uploaded first. Please wait for upload to complete or select the file again.';
            }
          }
          if (msgType === 'location' && (!formData.config.fixedMessage.latitude || !formData.config.fixedMessage.longitude)) {
            newErrors['config.fixedMessage.location'] = 'Location is required for location message type';
          }
          // Validate selected items (max 3)
          const selectedItemIds = formData.config?.fixedMessage?.selectedItems || [];
          if (selectedItemIds.length > 3) {
            newErrors['config.fixedMessage.selectedItems'] = 'Maximum 3 items can be selected';
          }
        }
        break;

      case 'data_set':
        if (!formData.config?.repliesSetId) {
          newErrors['config.repliesSetId'] = 'Replies set is required';
        }
        break;

      case 'flow':
        if (!formData.config?.flowId) {
          newErrors['config.flowId'] = 'A flow is required';
        }
        break;

      case 'product':
        if (!formData.config?.productId) {
          newErrors['config.productId'] = 'Product is required';
        }
        break;

      case 'catalog':
        if (!formData.config?.catalogId) {
          newErrors['config.catalogId'] = 'Catalog is required';
        }
        break;

      case 'webhook':
        if (!formData.config?.webhook?.channel || !String(formData.config.webhook.channel).trim()) {
          newErrors['config.webhook.channel'] = 'Please select a predefined webhook channel';
        }
        break;

      case 'url':
        if (!formData.config?.url || !formData.config.url.trim()) {
          newErrors['config.url'] = 'URL is required';
        } else if (!isValidUrl(formData.config.url.trim())) {
          newErrors['config.url'] = 'Please enter a valid URL (must start with http:// or https://)';
        }
        break;

      case 'task':
        if (!formData.config?.task?.title) {
          newErrors['config.task.title'] = 'Task title is required';
        }
        break;

      case 'calendar':
        if (!formData.config?.calendar?.title) {
          newErrors['config.calendar.title'] = 'Calendar title is required';
        }
        if (!formData.config?.calendar?.startDate) {
          newErrors['config.calendar.startDate'] = 'Start date is required';
        }
        break;

      case 'assign_user':
        const userIds = formData.config?.assignUser?.userIds || 
                       (formData.config?.assignUser?.userId ? [formData.config.assignUser.userId] : []);
        if (userIds.length === 0) {
          newErrors['config.assignUser.userIds'] = 'At least one user is required';
        }
        break;

      case 'assign_department':
        const departmentIds = formData.config?.assignDepartment?.departmentIds || 
                             (formData.config?.assignDepartment?.departmentId ? [formData.config.assignDepartment.departmentId] : []);
        if (departmentIds.length === 0) {
          newErrors['config.assignDepartment.departmentIds'] = 'At least one department is required';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    // Final check: reject blob URLs before submitting
    if (formData.type === 'fixed_message' && formData.config?.fixedMessage?.mediaUrl) {
      const mediaUrl = formData.config.fixedMessage.mediaUrl;
      if (mediaUrl.startsWith('blob:')) {
        Alert.alert(
          'Media Not Uploaded',
          'The media file has not been uploaded yet. Please wait for the upload to complete or select the file again.',
          [{ text: 'OK' }]
        );
        // Set error
        setErrors((prev) => ({
          ...prev,
          'config.fixedMessage.mediaUrl': 'Media file must be uploaded first. Please wait for upload to complete or select the file again.'
        }));
        return;
      }
    }
    
    if (validate()) {
      onSubmit({
        ...formData,
        setIds: selectedSetIds,
      });
    }
  };

  const handleSetToggle = (setId: string) => {
    const newSelection = selectedSetIds.includes(setId)
      ? selectedSetIds.filter((id) => id !== setId)
      : [...selectedSetIds, setId];
    setSelectedSetIds(newSelection);
    updateField('setIds', newSelection);
  };

  const filteredSets = availableRepliesSets.filter((set) =>
    set.name.toLowerCase().includes(setsSearchQuery.toLowerCase())
  );

  const filteredCatalogues = catalogues.filter((catalog) =>
    catalog.name.toLowerCase().includes(catalogSearchQuery.toLowerCase())
  );

  const filteredRepliesSets = allAvailableSets.filter((set) =>
    set.name.toLowerCase().includes(repliesSetSearchQuery.toLowerCase())
  );

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(productSearchQuery.toLowerCase())
  );

  const filteredCalendars = calendars.filter((calendar) =>
    calendar.name.toLowerCase().includes(calendarSearchQuery.toLowerCase())
  );

  const filteredItems = availableItems.filter((item) =>
    item.displayText.toLowerCase().includes(itemSearchQuery.toLowerCase())
  );

  const handleCatalogSelect = (catalogId: string) => {
    updateConfigField('catalogId', catalogId);
    setShowCatalogSelector(false);
    setCatalogSearchQuery('');
  };

  const handleRepliesSetSelect = (setId: string) => {
    updateConfigField('repliesSetId', setId);
    setShowRepliesSetSelector(false);
    setRepliesSetSearchQuery('');
  };

  const handleProductSelect = (productId: string) => {
    updateConfigField('productId', productId);
    setShowProductSelector(false);
    setProductSearchQuery('');
  };

  const handleCalendarSelect = (calendarId: string) => {
    updateNestedConfigField('calendar', 'calendarId', calendarId);
    setShowCalendarSelector(false);
    setCalendarSearchQuery('');
  };

  const handleItemSelect = (itemId: string) => {
    const currentItemIds = formData.config?.fixedMessage?.selectedItems || [];
    
    // Check if already selected
    if (currentItemIds.includes(itemId)) {
      return; // Already selected
    }
    
    // Check max limit
    if (currentItemIds.length >= 3) {
      Alert.alert('Maximum Limit', 'You can only select up to 3 items');
      return;
    }
    
    // Add item
    const updatedItemIds = [...currentItemIds, itemId];
    updateNestedConfigField('fixedMessage', 'selectedItems', updatedItemIds);
    
    // Update selected items display
    const selectedItem = availableItems.find(item => item._id === itemId);
    if (selectedItem) {
      setSelectedItems([...selectedItems, selectedItem]);
    }
    
    setShowItemSelector(false);
    setItemSearchQuery('');
  };

  const handleVoiceRecordingComplete = async (audioUri: string, duration: number) => {
    try {
      setShowVoiceRecorder(false);
      setUploadingMedia(true);
      
      // Upload the recorded audio file
      // Use consistent itemId (from initialData or temp ID generated once per session)
      const uploadResponse = await apiService.uploadMedia(
        audioUri,
        `voice_note_${Date.now()}.ogg`,
        'audio/ogg',
        'default-whatsapp',
        tempItemId
      );
      
      if (uploadResponse.statusCode === 1000 && uploadResponse.data) {
        // Convert Firebase Storage URL to proxy URL before saving
        const mediaType = uploadResponse.data.mediaType || 'audio/ogg';
        const proxyUrl = convertToProxyUrl(uploadResponse.data.mediaUrl, mediaType);
        updateNestedConfigField('fixedMessage', 'mediaUrl', proxyUrl);
        updateNestedConfigField('fixedMessage', 'mediaType', mediaType);
        updateNestedConfigField('fixedMessage', 'mediaName', uploadResponse.data.mediaName || `voice_note_${Date.now()}.ogg`);
        updateNestedConfigField('fixedMessage', 'mediaSize', uploadResponse.data.mediaSize || 0);
      } else {
        Alert.alert('Upload Failed', uploadResponse.errors?.[0]?.message || 'Failed to upload voice recording');
      }
    } catch (error: any) {
      console.error('Error uploading voice recording:', error);
      Alert.alert('Error', error.message || 'Failed to upload voice recording');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleVoiceRecordingCancel = () => {
    setShowVoiceRecorder(false);
  };

  const handleUserSelect = useCallback(async (userIds: string[]) => {
    if (userIds.length > 0) {
      // Store as userIds array (preferred) and also userId for backward compatibility
      updateNestedConfigField('assignUser', 'userIds', userIds);
      updateNestedConfigField('assignUser', 'userId', userIds[0]); // Keep for backward compatibility
      // Load users details to display
      if (currentBusiness?._id) {
        await loadUsersDetails(userIds);
      }
    } else {
      updateNestedConfigField('assignUser', 'userIds', []);
      updateNestedConfigField('assignUser', 'userId', '');
      setSelectedUsers([]);
    }
    setShowUserSelector(false);
  }, [currentBusiness?._id, loadUsersDetails]);

  const handleDepartmentSelect = useCallback(async (departmentIds: string[]) => {
    if (departmentIds.length > 0) {
      // Store as departmentIds array (preferred) and also departmentId for backward compatibility
      updateNestedConfigField('assignDepartment', 'departmentIds', departmentIds);
      updateNestedConfigField('assignDepartment', 'departmentId', departmentIds[0]); // Keep for backward compatibility
      // Load departments details to display
      if (currentBusiness?._id) {
        await loadDepartmentsDetails(departmentIds);
      }
    } else {
      updateNestedConfigField('assignDepartment', 'departmentIds', []);
      updateNestedConfigField('assignDepartment', 'departmentId', '');
      setSelectedDepartments([]);
    }
    setShowDepartmentSelector(false);
  }, [currentBusiness?._id, loadDepartmentsDetails]);

  // Expose submit function via ref
  React.useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const updateField = (field: keyof QuickReplyItemFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors[field as string]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }
  };

  const updateConfigField = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value,
      },
    }));
    // Clear errors
    if (errors[`config.${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`config.${field}`];
        return newErrors;
      });
    }
  };

  const updateNestedConfigField = (parent: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [parent]: {
          ...(prev.config[parent] || {}),
          [field]: value,
        },
      },
    }));
    // Clear errors
    if (errors[`config.${parent}.${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`config.${parent}.${field}`];
        return newErrors;
      });
    }
  };

  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case 'fixed_message':
        const messageType = formData.config?.fixedMessage?.messageType || 'text';
        return (
          <>
            <View style={styles.field}>
              <Text style={styles.label}>Message Type *</Text>
              <View style={styles.messageTypeGrid}>
                {[
                  { value: 'text', label: 'Text Only', icon: 'chatbubble-outline' },
                  { value: 'image', label: 'Image', icon: 'image-outline' },
                  { value: 'video', label: 'Video', icon: 'videocam-outline' },
                  { value: 'audio', label: 'Voice', icon: 'mic-outline' },
                  { value: 'document', label: 'File', icon: 'document-text-outline' },
                  { value: 'location', label: 'Location', icon: 'location-outline' },
                ].map((type) => (
                  <TouchableOpacity
                    key={type.value}
                    style={[
                      styles.messageTypeOption,
                      messageType === type.value && styles.messageTypeOptionSelected,
                    ]}
                    onPress={() => {
                      updateNestedConfigField('fixedMessage', 'messageType', type.value);
                      // Clear media/location data when switching types
                      if (type.value !== 'location') {
                        updateNestedConfigField('fixedMessage', 'latitude', undefined);
                        updateNestedConfigField('fixedMessage', 'longitude', undefined);
                        updateNestedConfigField('fixedMessage', 'address', undefined);
                      }
                      if (type.value !== 'image' && type.value !== 'video' && type.value !== 'audio' && type.value !== 'document') {
                        updateNestedConfigField('fixedMessage', 'mediaUrl', undefined);
                        updateNestedConfigField('fixedMessage', 'mediaType', undefined);
                        updateNestedConfigField('fixedMessage', 'mediaName', undefined);
                        updateNestedConfigField('fixedMessage', 'mediaSize', undefined);
                      }
                    }}
                  >
                    <Ionicons 
                      name={type.icon as any} 
                      size={24} 
                      color={messageType === type.value ? colors.primary : colors.text.secondary} 
                    />
                    <Text style={[
                      styles.messageTypeLabel,
                      messageType === type.value && styles.messageTypeLabelSelected,
                    ]}>
                      {type.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              {errors['config.fixedMessage.messageType'] && (
                <Text style={styles.errorText}>{errors['config.fixedMessage.messageType']}</Text>
              )}
            </View>

            {/* Text Message (for text type or caption for media/location) */}
            {(messageType === 'text' || messageType === 'image' || messageType === 'video' || messageType === 'location') && (
              <View style={styles.field}>
                <RichTextEditor
                  label={messageType === 'text' ? 'Text Message *' : 'Caption (Optional)'}
                  value={formData.config?.fixedMessage?.text || ''}
                  onChange={(text) => updateNestedConfigField('fixedMessage', 'text', text)}
                  placeholder={messageType === 'text' ? 'Enter the text message to send' : 'Enter caption for media/location'}
                  maxLength={4096}
                />
                {errors['config.fixedMessage.text'] && (
                  <Text style={styles.errorText}>{errors['config.fixedMessage.text']}</Text>
                )}
              </View>
            )}

            {/* Media File Selection (for image, video, audio, document) */}
            {(messageType === 'image' || messageType === 'video' || messageType === 'audio' || messageType === 'document') && (
              <View style={styles.field}>
                <Text style={styles.label}>
                  {messageType === 'image' ? 'Image' : 
                   messageType === 'video' ? 'Video' : 
                   messageType === 'audio' ? 'Voice Note' : 'Document'} *
                </Text>
                <TouchableOpacity
                  style={[styles.selectButton, errors['config.fixedMessage.mediaUrl'] && styles.selectButtonError]}
                  onPress={async () => {
                    try {
                      setUploadingMedia(true);
                      let mediaResult: MediaResult | null = null;
                      
                      if (messageType === 'image') {
                        mediaResult = await MediaService.pickImageFromGallery();
                      } else if (messageType === 'video') {
                        // Use ImagePicker with video media type
                        const result = await launchImageLibraryAsync({
                          mediaTypes: 'videos',
                          allowsEditing: true,
                          quality: 0.8,
                        });
                        if (!result.canceled && result.assets[0]) {
                          // Validate video file size (16MB = 16 * 1024 * 1024 bytes for WhatsApp)
                          const maxVideoSizeBytes = 16 * 1024 * 1024;
                          const videoFileSize = result.assets[0].fileSize || 0;
                          
                          if (videoFileSize > maxVideoSizeBytes) {
                            Alert.alert(
                              'File Too Large',
                              `Video file size (${(videoFileSize / (1024 * 1024)).toFixed(2)}MB) exceeds WhatsApp's maximum limit of 16MB. Please select a smaller video.`,
                              [{ text: 'OK' }]
                            );
                            setUploadingMedia(false);
                            return;
                          }
                          
                          mediaResult = {
                            uri: result.assets[0].uri,
                            type: 'video',
                            fileName: result.assets[0].fileName || `video_${Date.now()}.mp4`,
                            fileSize: videoFileSize,
                            width: result.assets[0].width,
                            height: result.assets[0].height,
                            mimeType: result.assets[0].type || 'video/mp4',
                          };
                        }
                      } else if (messageType === 'audio') {
                        // For audio, show voice recorder instead of document picker
                        setUploadingMedia(false);
                        setShowVoiceRecorder(true);
                        return;
                      } else if (messageType === 'document') {
                        mediaResult = await MediaService.pickDocument();
                      }
                      
                      if (mediaResult) {
                        // Validate video file size before upload (already validated for video above)
                        // Upload media file to get URL
                        // Use consistent itemId (from initialData or temp ID generated once per session)
                        const uploadResponse = await apiService.uploadMedia(
                          mediaResult.uri,
                          mediaResult.fileName || `${messageType}_${Date.now()}`,
                          mediaResult.mimeType || 'application/octet-stream',
                          'default-whatsapp',
                          tempItemId
                        );
                        
                        if (uploadResponse.statusCode === 1000 && uploadResponse.data) {
                          // Convert Firebase Storage URL to proxy URL before saving
                          const mediaType = uploadResponse.data.mediaType || mediaResult.mimeType || 'application/octet-stream';
                          const proxyUrl = convertToProxyUrl(uploadResponse.data.mediaUrl, mediaType);
                          
                          // Double-check: reject blob URLs even after conversion
                          if (proxyUrl.startsWith('blob:')) {
                            console.error('❌ [QuickReplyItemForm] Upload returned blob URL - clearing media');
                            updateNestedConfigField('fixedMessage', 'mediaUrl', undefined);
                            updateNestedConfigField('fixedMessage', 'mediaType', undefined);
                            updateNestedConfigField('fixedMessage', 'mediaName', undefined);
                            updateNestedConfigField('fixedMessage', 'mediaSize', undefined);
                            Alert.alert('Upload Error', 'Media upload failed. Please try selecting the file again.');
                            return;
                          }
                          
                          updateNestedConfigField('fixedMessage', 'mediaUrl', proxyUrl);
                          updateNestedConfigField('fixedMessage', 'mediaType', mediaType);
                          updateNestedConfigField('fixedMessage', 'mediaName', uploadResponse.data.mediaName || mediaResult.fileName);
                          updateNestedConfigField('fixedMessage', 'mediaSize', uploadResponse.data.mediaSize || mediaResult.fileSize);
                        } else {
                          // Clear any blob URLs on upload failure
                          const currentMediaUrl = formData.config?.fixedMessage?.mediaUrl;
                          if (currentMediaUrl && currentMediaUrl.startsWith('blob:')) {
                            updateNestedConfigField('fixedMessage', 'mediaUrl', undefined);
                            updateNestedConfigField('fixedMessage', 'mediaType', undefined);
                            updateNestedConfigField('fixedMessage', 'mediaName', undefined);
                            updateNestedConfigField('fixedMessage', 'mediaSize', undefined);
                          }
                          Alert.alert('Upload Failed', uploadResponse.errors?.[0]?.message || 'Failed to upload media file. Please try again.');
                        }
                      }
                    } catch (error: any) {
                      console.error('Error selecting/uploading media:', error);
                      Alert.alert('Error', error.message || 'Failed to select media file');
                    } finally {
                      setUploadingMedia(false);
                    }
                  }}
                  disabled={uploadingMedia}
                >
                  {uploadingMedia ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : (
                    <>
                      <Text style={[styles.selectButtonText, !formData.config?.fixedMessage?.mediaUrl && styles.selectButtonPlaceholder]}>
                        {formData.config?.fixedMessage?.mediaUrl
                          ? formData.config.fixedMessage.mediaName || 'Media selected'
                          : `Select ${messageType === 'image' ? 'Image' : messageType === 'video' ? 'Video' : messageType === 'audio' ? 'Voice' : 'Document'}`}
                      </Text>
                      <Ionicons name="chevron-down" size={20} color={colors.textLight} />
                    </>
                  )}
                </TouchableOpacity>
                {formData.config?.fixedMessage?.mediaUrl && (
                  <View style={styles.selectedMediaContainer}>
                    <Ionicons 
                      name={messageType === 'image' ? 'image' : messageType === 'video' ? 'videocam' : messageType === 'audio' ? 'mic' : 'document'} 
                      size={20} 
                      color={colors.primary} 
                    />
                    <Text style={styles.selectedMediaText}>
                      {formData.config.fixedMessage.mediaName || 'Media file selected'}
                    </Text>
                    <TouchableOpacity
                      onPress={() => {
                        updateNestedConfigField('fixedMessage', 'mediaUrl', undefined);
                        updateNestedConfigField('fixedMessage', 'mediaType', undefined);
                        updateNestedConfigField('fixedMessage', 'mediaName', undefined);
                        updateNestedConfigField('fixedMessage', 'mediaSize', undefined);
                      }}
                    >
                      <Ionicons name="close-circle" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                )}
                {errors['config.fixedMessage.mediaUrl'] && (
                  <Text style={styles.errorText}>{errors['config.fixedMessage.mediaUrl']}</Text>
                )}
              </View>
            )}

            {/* Location Selection */}
            {messageType === 'location' && (
              <View style={styles.field}>
                <Text style={styles.label}>Location *</Text>
                <TouchableOpacity
                  style={[styles.selectButton, errors['config.fixedMessage.location'] && styles.selectButtonError]}
                  onPress={() => setShowLocationPicker(true)}
                >
                  <Text style={[styles.selectButtonText, (!formData.config?.fixedMessage?.latitude || !formData.config?.fixedMessage?.longitude) && styles.selectButtonPlaceholder]}>
                    {formData.config?.fixedMessage?.address || 'Select location'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={colors.textLight} />
                </TouchableOpacity>
                {errors['config.fixedMessage.location'] && (
                  <Text style={styles.errorText}>{errors['config.fixedMessage.location']}</Text>
                )}
              </View>
            )}

            {/* Selected Items to Display */}
            <View style={styles.field}>
              <View style={styles.sectionHeader}>
                <Text style={styles.label}>Display Items (Optional)</Text>
                <TouchableOpacity
                  style={styles.addUsersButton}
                  onPress={() => setShowItemSelector(true)}
                  activeOpacity={0.7}
                  disabled={selectedItems.length >= 3}
                >
                  <Ionicons name="add-circle-outline" size={18} color={selectedItems.length >= 3 ? colors.textLight : colors.primary} />
                  <Text style={[styles.addUsersButtonText, selectedItems.length >= 3 && { color: colors.textLight }]}>
                    Add Items
                  </Text>
                </TouchableOpacity>
              </View>
              <Text style={styles.helperText}>
                Select up to 3 quick reply items to display as buttons with this message
              </Text>
              
              {selectedItems.length === 0 ? (
                <View style={styles.emptyUsersContainer}>
                  <Ionicons name="list-outline" size={32} color={colors.text.secondary} />
                  <Text style={styles.emptyUsersText}>No items selected</Text>
                  <Text style={styles.emptyUsersSubtext}>Add items to display as buttons with the message</Text>
                </View>
              ) : (
                <View style={styles.usersList}>
                  {selectedItems.map((item) => (
                    <View key={item._id} style={styles.userItem}>
                      <View style={styles.userItemInfo}>
                        <View style={styles.userItemAvatar}>
                          {item.icon ? (
                            <Text style={styles.userItemAvatarText}>{item.icon}</Text>
                          ) : (
                            <Ionicons name="chatbubble-outline" size={20} color={colors.primary} />
                          )}
                        </View>
                        <View style={styles.userItemDetails}>
                          <Text style={styles.userItemName}>{item.displayText}</Text>
                          <Text style={styles.userItemEmail}>{item.type}</Text>
                        </View>
                      </View>
                      <TouchableOpacity
                        style={styles.removeUserButton}
                        onPress={() => {
                          const currentItemIds = formData.config?.fixedMessage?.selectedItems || [];
                          const updatedItemIds = currentItemIds.filter((id: string) => id !== item._id);
                          updateNestedConfigField('fixedMessage', 'selectedItems', updatedItemIds);
                          setSelectedItems(selectedItems.filter(i => i._id !== item._id));
                        }}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Ionicons name="close-circle" size={20} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </>
        );

      case 'data_set':
        return (
          <>
            <View style={styles.field}>
              <Text style={styles.label}>Replies Set *</Text>
              <TouchableOpacity
                style={[styles.selectButton, errors['config.repliesSetId'] && styles.selectButtonError]}
                onPress={() => setShowRepliesSetSelector(true)}
              >
                <Text style={[styles.selectButtonText, !formData.config?.repliesSetId && styles.selectButtonPlaceholder]}>
                  {formData.config?.repliesSetId
                    ? allAvailableSets.find((s) => s._id === formData.config.repliesSetId)?.name || 'Select replies set'
                    : 'Select replies set'}
                </Text>
                <Ionicons name="chevron-down" size={20} color={colors.textLight} />
              </TouchableOpacity>
              {errors['config.repliesSetId'] && (
                <Text style={styles.errorText}>{errors['config.repliesSetId']}</Text>
              )}
            </View>
            <View style={styles.field}>
              <RichTextEditor
                label="Body Text (Optional)"
                value={formData.bodyText || ''}
                onChange={(text) => setFormData({ ...formData, bodyText: text })}
                placeholder="Text to display when this item triggers the nested set"
                maxLength={4096}
              />
              <Text style={styles.helperText}>
                This text will be shown as the body of the nested replies set. If empty, the button text will be used. (WhatsApp limit: 4096 characters)
              </Text>
            </View>
          </>
        );

      case 'flow':
        return (
          <>
            <View style={styles.field}>
              <Text style={styles.label}>Flow *</Text>
              <TouchableOpacity
                style={[styles.selectButton, errors['config.flowId'] && styles.selectButtonError]}
                onPress={() => setShowFlowSelector(true)}
              >
                <Text style={[styles.selectButtonText, !formData.config?.flowId && styles.selectButtonPlaceholder]}>
                  {formData.config?.flowId
                    ? availableFlows.find((f) => f._id === formData.config.flowId)?.name || 'Select flow'
                    : 'Select flow'}
                </Text>
                <Ionicons name="chevron-down" size={20} color={colors.textLight} />
              </TouchableOpacity>
              {errors['config.flowId'] && (
                <Text style={styles.errorText}>{errors['config.flowId']}</Text>
              )}
              <Text style={styles.helperText}>
                When a contact taps this item, the selected flow starts — its entry node sees this item's tap as its input.
              </Text>
            </View>
            <Modal visible={showFlowSelector} transparent animationType="slide" onRequestClose={() => setShowFlowSelector(false)}>
              <View style={styles.modalOverlay}>
                <View style={styles.modalContent}>
                  <View style={styles.modalHeader}>
                    <Text style={styles.modalTitle}>Select Flow</Text>
                    <TouchableOpacity onPress={() => setShowFlowSelector(false)}>
                      <Ionicons name="close" size={24} color={colors.textDark} />
                    </TouchableOpacity>
                  </View>
                  {loadingFlows ? (
                    <View style={styles.loadingContainer}>
                      <ActivityIndicator size="large" color={colors.primary} />
                      <Text style={styles.loadingText}>Loading flows...</Text>
                    </View>
                  ) : (
                    <FlatList
                      data={availableFlows}
                      keyExtractor={(item) => item._id}
                      renderItem={({ item }) => {
                        const isSelected = formData.config?.flowId === item._id;
                        return (
                          <TouchableOpacity
                            style={[styles.typeOption, isSelected && styles.typeOptionSelected]}
                            onPress={() => { updateConfigField('flowId', item._id); setShowFlowSelector(false); }}
                          >
                            <Ionicons name="git-pull-request-outline" size={24} color={colors.primary} />
                            <View style={styles.typeOptionContent}>
                              <Text style={styles.typeOptionLabel}>{item.name}</Text>
                            </View>
                          </TouchableOpacity>
                        );
                      }}
                    />
                  )}
                </View>
              </View>
            </Modal>
          </>
        );

      case 'product':
        const selectedProduct = products.find((p) => p._id === formData.config?.productId);
        return (
          <>
            <View style={styles.field}>
              <Text style={styles.label}>Product *</Text>
              <TouchableOpacity
                style={[styles.selectButton, errors['config.productId'] && styles.selectButtonError]}
                onPress={() => setShowProductSelector(true)}
              >
                <Text style={[styles.selectButtonText, !formData.config?.productId && styles.selectButtonPlaceholder]}>
                  {formData.config?.productId
                    ? selectedProduct?.name || 'Product selected'
                    : 'Select product'}
                </Text>
                <Ionicons name="chevron-down" size={20} color={colors.textLight} />
              </TouchableOpacity>
              {errors['config.productId'] && (
                <Text style={styles.errorText}>{errors['config.productId']}</Text>
              )}
            </View>
            
            {/* Order Customization Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Order Message Customization</Text>
              
              {/* Language Picker */}
              <View style={styles.field}>
                <Text style={styles.label}>Language *</Text>
                <View style={styles.languageButtons}>
                  {(['ar', 'en'] as const).map((lang) => (
                    <TouchableOpacity
                      key={lang}
                      style={[
                        styles.languageButton,
                        formData.config?.orderCustomization?.language === lang && styles.languageButtonActive,
                      ]}
                      onPress={() => updateNestedConfigField('orderCustomization', 'language', lang)}
                    >
                      <Text
                        style={[
                          styles.languageButtonText,
                          formData.config?.orderCustomization?.language === lang && styles.languageButtonTextActive,
                        ]}
                      >
                        {lang.toUpperCase()}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              {/* Placement Message */}
              <View style={styles.field}>
                <RichTextEditor
                  label="Placement Message (50 chars max)"
                  value={formData.config?.orderCustomization?.placementMessage || ''}
                  onChange={(text) => updateNestedConfigField('orderCustomization', 'placementMessage', text)}
                  placeholder="Enter placement message (e.g., ✅ Your order has been placed!)"
                  maxLength={50}
                />
                <Text style={styles.helperText}>
                  This message will be shown when order is placed. Order details will be appended automatically.
                </Text>
              </View>
              
              {/* Confirm Button Text */}
              <View style={styles.field}>
                <Input
                  label="Confirm Button Text (15 chars max)"
                  value={formData.config?.orderCustomization?.confirmButtonText || ''}
                  onChangeText={(text) => {
                    if (text.length <= 15) {
                      updateNestedConfigField('orderCustomization', 'confirmButtonText', text);
                    }
                  }}
                  placeholder="✅ Yes"
                  maxLength={15}
                />
              </View>
              
              {/* Cancel Button Text */}
              <View style={styles.field}>
                <Input
                  label="Cancel Button Text (15 chars max)"
                  value={formData.config?.orderCustomization?.cancelButtonText || ''}
                  onChangeText={(text) => {
                    if (text.length <= 15) {
                      updateNestedConfigField('orderCustomization', 'cancelButtonText', text);
                    }
                  }}
                  placeholder="❌ No"
                  maxLength={15}
                />
              </View>
              
              {/* Status change message — used for every order status change (confirm, cancel, etc.) */}
              <View style={styles.field}>
                <RichTextEditor
                  label="Status change message"
                  value={formData.config?.orderCustomization?.statusChangeMessage || ''}
                  onChange={(text) => updateNestedConfigField('orderCustomization', 'statusChangeMessage', text)}
                  placeholder="e.g. Your order has been {{status}}."
                  maxLength={200}
                />
                <Text style={styles.helperText}>
                  Used for every order status change (confirmed, cancelled, etc.). Use {'{{status}}'} where the status should appear.
                </Text>
              </View>
              
              {/* Track Orders Button Text */}
              <View style={styles.field}>
                <Input
                  label="Track Orders Button Text (15 chars max)"
                  value={formData.config?.orderCustomization?.trackOrdersButtonText || ''}
                  onChangeText={(text) => {
                    if (text.length <= 15) {
                      updateNestedConfigField('orderCustomization', 'trackOrdersButtonText', text);
                    }
                  }}
                  placeholder="📦 Track Orders"
                  maxLength={15}
                />
              </View>
              {/* Track Orders URL (supports {orderId} and {orderNumber} placeholders) */}
              <View style={styles.field}>
                <Input
                  label="Track Orders URL"
                  value={formData.config?.orderCustomization?.trackOrdersUrl || ''}
                  onChangeText={(text) => updateNestedConfigField('orderCustomization', 'trackOrdersUrl', text)}
                  placeholder="https://example.com/order/{orderId}"
                />
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', marginTop: 6, borderWidth: 1, borderColor: colors.primary, borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5 }}
                  onPress={() => {
                    const base = Platform.OS === 'web' && typeof window !== 'undefined'
                      ? window.location.origin
                      : 'https://app.sendifier.com';
                    const url = `${base}/track-order?businessId=${currentBusiness?._id || '{businessId}'}&orderNumber={orderNumber}&contactId={contactId}`;
                    updateNestedConfigField('orderCustomization', 'trackOrdersUrl', url);
                  }}
                  activeOpacity={0.7}>
                  <Ionicons name="link-outline" size={14} color={colors.primary} />
                  <Text style={{ fontSize: 12, color: colors.primary, fontFamily: typography.fontFamily.medium }}>Use DISLIX Tracking Page</Text>
                </TouchableOpacity>
              </View>

              {/* View Product Button Text */}
              <View style={styles.field}>
                <Input
                  label="View Product Button Text (20 chars max)"
                  value={formData.config?.orderCustomization?.viewProductButtonText || ''}
                  onChangeText={(text) => {
                    if (text.length <= 20) {
                      updateNestedConfigField('orderCustomization', 'viewProductButtonText', text);
                    }
                  }}
                  placeholder="🛍️ View Product"
                  maxLength={20}
                />
              </View>
            </View>
          </>
        );

      case 'catalog':
        return (
          <>
            <View style={styles.field}>
              <Text style={styles.label}>Catalog *</Text>
              <TouchableOpacity
                style={[styles.selectButton, errors['config.catalogId'] && styles.selectButtonError]}
                onPress={() => setShowCatalogSelector(true)}
              >
                <Text style={[styles.selectButtonText, !formData.config?.catalogId && styles.selectButtonPlaceholder]}>
                  {formData.config?.catalogId
                    ? catalogues.find((c) => c._id === formData.config.catalogId)?.name || 'Select catalog'
                    : 'Select catalog'}
                </Text>
                <Ionicons name="chevron-down" size={20} color={colors.textLight} />
              </TouchableOpacity>
              {errors['config.catalogId'] && (
                <Text style={styles.errorText}>{errors['config.catalogId']}</Text>
              )}
            </View>
            
            {/* Order Customization Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Order Message Customization</Text>
              
              {/* Language Picker */}
              <View style={styles.field}>
                <Text style={styles.label}>Language *</Text>
                <View style={styles.languageButtons}>
                  {(['ar', 'en'] as const).map((lang) => (
                    <TouchableOpacity
                      key={lang}
                      style={[
                        styles.languageButton,
                        formData.config?.orderCustomization?.language === lang && styles.languageButtonActive,
                      ]}
                      onPress={() => updateNestedConfigField('orderCustomization', 'language', lang)}
                    >
                      <Text
                        style={[
                          styles.languageButtonText,
                          formData.config?.orderCustomization?.language === lang && styles.languageButtonTextActive,
                        ]}
                      >
                        {lang.toUpperCase()}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              {/* Placement Message */}
              <View style={styles.field}>
                <RichTextEditor
                  label="Placement Message (50 chars max)"
                  value={formData.config?.orderCustomization?.placementMessage || ''}
                  onChange={(text) => updateNestedConfigField('orderCustomization', 'placementMessage', text)}
                  placeholder="Enter placement message (e.g., ✅ Your order has been placed!)"
                  maxLength={50}
                />
                <Text style={styles.helperText}>
                  This message will be shown when order is placed. Order details will be appended automatically.
                </Text>
              </View>
              
              {/* Confirm Button Text */}
              <View style={styles.field}>
                <Input
                  label="Confirm Button Text (15 chars max)"
                  value={formData.config?.orderCustomization?.confirmButtonText || ''}
                  onChangeText={(text) => {
                    if (text.length <= 15) {
                      updateNestedConfigField('orderCustomization', 'confirmButtonText', text);
                    }
                  }}
                  placeholder="✅ Yes"
                  maxLength={15}
                />
              </View>
              
              {/* Cancel Button Text */}
              <View style={styles.field}>
                <Input
                  label="Cancel Button Text (15 chars max)"
                  value={formData.config?.orderCustomization?.cancelButtonText || ''}
                  onChangeText={(text) => {
                    if (text.length <= 15) {
                      updateNestedConfigField('orderCustomization', 'cancelButtonText', text);
                    }
                  }}
                  placeholder="❌ No"
                  maxLength={15}
                />
              </View>
              
              {/* Status change message — used for every order status change (confirm, cancel, etc.) */}
              <View style={styles.field}>
                <RichTextEditor
                  label="Status change message"
                  value={formData.config?.orderCustomization?.statusChangeMessage || ''}
                  onChange={(text) => updateNestedConfigField('orderCustomization', 'statusChangeMessage', text)}
                  placeholder="e.g. Your order has been {{status}}."
                  maxLength={200}
                />
                <Text style={styles.helperText}>
                  Used for every order status change (confirmed, cancelled, etc.). Use {'{{status}}'} where the status should appear.
                </Text>
              </View>
              
              {/* Track Orders Button Text */}
              <View style={styles.field}>
                <Input
                  label="Track Orders Button Text (15 chars max)"
                  value={formData.config?.orderCustomization?.trackOrdersButtonText || ''}
                  onChangeText={(text) => {
                    if (text.length <= 15) {
                      updateNestedConfigField('orderCustomization', 'trackOrdersButtonText', text);
                    }
                  }}
                  placeholder="📦 Track Orders"
                  maxLength={15}
                />
              </View>
              {/* Track Orders URL (supports {orderId} and {orderNumber} placeholders) */}
              <View style={styles.field}>
                <Input
                  label="Track Orders URL"
                  value={formData.config?.orderCustomization?.trackOrdersUrl || ''}
                  onChangeText={(text) => updateNestedConfigField('orderCustomization', 'trackOrdersUrl', text)}
                  placeholder="https://example.com/order/{orderId}"
                />
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', marginTop: 6, borderWidth: 1, borderColor: colors.primary, borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5 }}
                  onPress={() => {
                    const base = Platform.OS === 'web' && typeof window !== 'undefined'
                      ? window.location.origin
                      : 'https://app.sendifier.com';
                    const url = `${base}/track-order?businessId=${currentBusiness?._id || '{businessId}'}&orderNumber={orderNumber}&contactId={contactId}`;
                    updateNestedConfigField('orderCustomization', 'trackOrdersUrl', url);
                  }}
                  activeOpacity={0.7}>
                  <Ionicons name="link-outline" size={14} color={colors.primary} />
                  <Text style={{ fontSize: 12, color: colors.primary, fontFamily: typography.fontFamily.medium }}>Use DISLIX Tracking Page</Text>
                </TouchableOpacity>
              </View>

              {/* View Catalog Button Text */}
              <View style={styles.field}>
                <Input
                  label="View Catalog Button Text (20 chars max)"
                  value={formData.config?.orderCustomization?.viewCatalogButtonText || ''}
                  onChangeText={(text) => {
                    if (text.length <= 20) {
                      updateNestedConfigField('orderCustomization', 'viewCatalogButtonText', text);
                    }
                  }}
                  placeholder="📦 View Catalog"
                  maxLength={20}
                />
              </View>
            </View>
          </>
        );

      case 'url':
        return (
          <>
            <View style={styles.field}>
              <Input
                label="URL *"
                value={formData.config?.url || ''}
                onChangeText={(url) => updateConfigField('url', url)}
                placeholder="https://example.com"
                error={errors['config.url']}
                autoCapitalize="none"
                keyboardType="url"
                autoCorrect={false}
              />
              {formData.config?.url && isValidUrl(formData.config.url.trim()) && (
                <Text style={styles.helperText}>Valid URL format</Text>
              )}
            </View>
            
            <View style={styles.field}>
              <RichTextEditor
                label="Message Body Text (Optional)"
                value={formData.config?.bodyText || ''}
                onChange={(text) => updateConfigField('bodyText', text)}
                placeholder="Enter message body text (e.g., Click the button below to visit our website)"
                maxLength={1024}
                showCharCount={true}
              />
              <Text style={styles.helperText}>
                This text will be shown in the interactive message when the URL item is sent. If not provided, the default text or channel settings custom message will be used.
              </Text>
            </View>
          </>
        );

      case 'webhook':
        return (
          <>
            <View style={styles.field}>
              <Text style={styles.label}>Developer Access Webhook Channel *</Text>
              {loadingWebhooks ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="small" color={colors.primary} />
                  <Text style={styles.loadingText}>Loading configured webhooks...</Text>
                </View>
              ) : availableWebhooks.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>No configured webhooks found.</Text>
                  <Text style={styles.helperText}>Please add a webhook from Developer Access settings first.</Text>
                </View>
              ) : (
                <View style={styles.methodButtons}>
                  {availableWebhooks.map((webhook) => {
                    const selectedWebhookId = formData.config?.webhook?.webhookId;
                    const isSelected = selectedWebhookId
                      ? selectedWebhookId === webhook._id
                      : formData.config?.webhook?.channel === webhook.channel;
                    const channelLabel = WEBHOOK_CHANNEL_LABELS[webhook.channel] || webhook.channel;
                    return (
                      <TouchableOpacity
                        key={webhook._id}
                        style={[styles.methodButton, isSelected && styles.methodButtonActive]}
                        onPress={() => {
                          updateNestedConfigField('webhook', 'webhookId', webhook._id);
                          updateNestedConfigField('webhook', 'title', webhook.title);
                          updateNestedConfigField('webhook', 'channel', webhook.channel);
                          updateNestedConfigField('webhook', 'skipChannelHooks', !!formData.config?.webhook?.skipChannelHooks);
                        }}
                      >
                        <Text style={[styles.methodButtonText, isSelected && styles.methodButtonTextActive]}>
                          {webhook.title || 'Untitled Webhook'}
                        </Text>
                        <Text style={styles.webhookOptionSubText}>{channelLabel}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
              {errors['config.webhook.channel'] && (
                <Text style={styles.errorText}>{errors['config.webhook.channel']}</Text>
              )}
              <Text style={styles.helperText}>
                The selected item will trigger the existing channel webhook configuration from Developer Access.
              </Text>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>Skip Channel Hooks</Text>
              <View style={styles.methodButtons}>
                {[
                  { value: false, label: 'No' },
                  { value: true, label: 'Yes' },
                ].map((option) => (
                  <TouchableOpacity
                    key={option.label}
                    style={[
                      styles.methodButton,
                      !!formData.config?.webhook?.skipChannelHooks === option.value && styles.methodButtonActive,
                    ]}
                    onPress={() => updateNestedConfigField('webhook', 'skipChannelHooks', option.value)}
                  >
                    <Text
                      style={[
                        styles.methodButtonText,
                        !!formData.config?.webhook?.skipChannelHooks === option.value && styles.methodButtonTextActive,
                      ]}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={styles.helperText}>
                When enabled, the regular inbound channel webhook will be skipped for this click and only this item webhook will run.
              </Text>
            </View>

            <View style={styles.field}>
              <Input
                label="Success Message"
                value={formData.config?.webhook?.responseMapping?.successText || ''}
                onChangeText={(text) =>
                  updateNestedConfigField('webhook', 'responseMapping', {
                    ...(formData.config?.webhook?.responseMapping || {}),
                    successText: text,
                  })
                }
                placeholder="Message to send if webhook succeeds"
              />
            </View>
            <View style={styles.field}>
              <Input
                label="Error Message"
                value={formData.config?.webhook?.responseMapping?.errorText || ''}
                onChangeText={(text) =>
                  updateNestedConfigField('webhook', 'responseMapping', {
                    ...(formData.config?.webhook?.responseMapping || {}),
                    errorText: text,
                  })
                }
                placeholder="Message to send if webhook fails"
              />
            </View>
          </>
        );

      case 'task':
        return (
          <>
            <View style={styles.field}>
              <Input
                label="Task Title *"
                value={formData.config?.task?.title || ''}
                onChangeText={(title) => updateNestedConfigField('task', 'title', title)}
                placeholder="Enter task title"
                error={errors['config.task.title']}
              />
            </View>
            <View style={styles.field}>
              <Input
                label="Task Description"
                value={formData.config?.task?.description || ''}
                onChangeText={(description) => updateNestedConfigField('task', 'description', description)}
                placeholder="Enter task description"
                multiline
                numberOfLines={3}
              />
            </View>
          </>
        );

      case 'calendar':
        const calendarMode = formData.config?.calendar?.mode || 'propose';
        const startDate = formData.config?.calendar?.startDate 
          ? (typeof formData.config.calendar.startDate === 'string' 
              ? new Date(formData.config.calendar.startDate) 
              : formData.config.calendar.startDate)
          : new Date();
        const endDate = formData.config?.calendar?.endDate 
          ? (typeof formData.config.calendar.endDate === 'string' 
              ? new Date(formData.config.calendar.endDate) 
              : formData.config.calendar.endDate)
          : new Date(startDate.getTime() + 60 * 60 * 1000);
        const timeSlots = formData.config?.calendar?.timeSlots || [];

        const selectedCalendar = calendars.find(c => c._id === formData.config?.calendar?.calendarId);
        
        return (
          <>
            <View style={styles.field}>
              <Text style={styles.label}>Calendar *</Text>
              <TouchableOpacity
                style={styles.selectButton}
                onPress={() => setShowCalendarSelector(true)}
                disabled={loadingCalendars}
              >
                <Text style={[styles.selectButtonText, !selectedCalendar && styles.selectButtonPlaceholder]}>
                  {loadingCalendars ? 'Loading calendars...' : selectedCalendar ? selectedCalendar.name : 'Select Calendar'}
                </Text>
                <Ionicons name="chevron-down" size={20} color={colors.textLight} />
              </TouchableOpacity>
            </View>
            
            <View style={styles.field}>
              <Text style={styles.label}>Mode *</Text>
              <View style={styles.methodButtons}>
                <TouchableOpacity
                  style={[
                    styles.methodButton,
                    calendarMode === 'propose' && styles.methodButtonActive
                  ]}
                  onPress={() => updateNestedConfigField('calendar', 'mode', 'propose')}
                >
                  <Text style={[
                    styles.methodButtonText,
                    calendarMode === 'propose' && styles.methodButtonTextActive
                  ]}>
                    Propose Time
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.methodButton,
                    calendarMode === 'pick' && styles.methodButtonActive
                  ]}
                  onPress={() => updateNestedConfigField('calendar', 'mode', 'pick')}
                >
                  <Text style={[
                    styles.methodButtonText,
                    calendarMode === 'pick' && styles.methodButtonTextActive
                  ]}>
                    User Picks
                  </Text>
                </TouchableOpacity>
              </View>
              <Text style={styles.helperText}>
                {calendarMode === 'propose' 
                  ? 'Business proposes specific time slots for user to choose from'
                  : 'User provides their preferred date and time'}
              </Text>
            </View>
            <View style={styles.field}>
              <Input
                label="Event Title *"
                value={formData.config?.calendar?.title || ''}
                onChangeText={(title) => updateNestedConfigField('calendar', 'title', title)}
                placeholder="Enter event title"
                error={errors['config.calendar.title']}
              />
            </View>
            <View style={styles.field}>
              <RichTextEditor
                label="Event Description"
                value={formData.config?.calendar?.description || ''}
                onChange={(description) => updateNestedConfigField('calendar', 'description', description)}
                placeholder="Enter event description"
                maxLength={4096}
              />
            </View>
            <View style={styles.field}>
              <RichTextEditor
                label="Body Text (Optional)"
                value={formData.config?.calendar?.bodyText || ''}
                onChange={(bodyText) => updateNestedConfigField('calendar', 'bodyText', bodyText)}
                placeholder="Text to display in the booking message body"
                maxLength={4096}
              />
              <Text style={styles.helperText}>
                This text will be shown as the body of the booking message. If empty, the event description will be used. (WhatsApp limit: 4096 characters)
              </Text>
            </View>
            <View style={styles.field}>
              <Input
                label="Button Text (Optional)"
                value={formData.config?.calendar?.buttonText || ''}
                onChangeText={(buttonText) => updateNestedConfigField('calendar', 'buttonText', buttonText)}
                placeholder="📅 Book Appointment"
              />
              <Text style={styles.helperText}>
                Text to display on the booking button. If empty, "📅 Book Appointment" will be used.
              </Text>
            </View>
            <View style={styles.field}>
              <RichTextEditor
                label="Success Message (Optional)"
                value={formData.config?.calendar?.successText || ''}
                onChange={(successText) => updateNestedConfigField('calendar', 'successText', successText)}
                placeholder="Your appointment has been confirmed!"
                maxLength={4096}
              />
              <Text style={styles.helperText}>
                This message will be displayed in the confirmation after booking. The business name will be shown as the header, and the booked date will be appended automatically. (WhatsApp limit: 4096 characters)
              </Text>
            </View>
            {calendarMode === 'propose' && (
              <View style={styles.field}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                  <Text style={styles.label}>Time Slots (Optional)</Text>
                  <TouchableOpacity
                    style={[styles.methodButton, { paddingHorizontal: spacing.md, paddingVertical: spacing.xs }]}
                    onPress={() => {
                      const now = new Date();
                      const newSlot = {
                        startDate: new Date(now.getTime() + 60 * 60 * 1000).toISOString(),
                        endDate: new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString(),
                        label: ''
                      };
                      const currentSlots = formData.config?.calendar?.timeSlots || [];
                      updateNestedConfigField('calendar', 'timeSlots', [...currentSlots, newSlot]);
                    }}
                  >
                    <Ionicons name="add" size={16} color={colors.primary} style={{ marginRight: spacing.xs }} />
                    <Text style={[styles.methodButtonText, { color: colors.primary }]}>Add Slot</Text>
                  </TouchableOpacity>
                </View>
                <Text style={styles.helperText}>
                  Add multiple time slots for users to choose from (max 3 will be shown as buttons)
                </Text>
                {timeSlots.map((slot: any, index: number) => {
                  const slotStart = typeof slot.startDate === 'string' ? new Date(slot.startDate) : new Date(slot.startDate);
                  const slotEnd = typeof slot.endDate === 'string' ? new Date(slot.endDate) : new Date(slot.endDate);
                  return (
                    <View key={index} style={[styles.field, { backgroundColor: colors.lightGray, padding: spacing.sm, borderRadius: 8, marginBottom: spacing.xs }]}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                        <Text style={[styles.label, { marginBottom: 0 }]}>Slot {index + 1}</Text>
                        <TouchableOpacity
                          onPress={() => {
                            const updatedSlots = timeSlots.filter((_: any, i: number) => i !== index);
                            updateNestedConfigField('calendar', 'timeSlots', updatedSlots);
                          }}
                        >
                          <Ionicons name="trash-outline" size={20} color={colors.error} />
                        </TouchableOpacity>
                      </View>
                      {Platform.OS === 'web' ? (
                        <>
                          <View style={{ flexDirection: 'row', gap: spacing.xs, marginBottom: spacing.xs }}>
                            <input
                              type="date"
                              value={slotStart.toISOString().split('T')[0]}
                              onChange={(e: any) => {
                                const selectedDate = new Date(e.target.value);
                                selectedDate.setHours(slotStart.getHours());
                                selectedDate.setMinutes(slotStart.getMinutes());
                                const updatedSlots = [...timeSlots];
                                updatedSlots[index] = {
                                  ...slot,
                                  startDate: selectedDate.toISOString()
                                };
                                updateNestedConfigField('calendar', 'timeSlots', updatedSlots);
                              }}
                              style={{
                                flex: 1,
                                padding: spacing.sm,
                                fontSize: typography.fontSize.base,
                                border: `1px solid ${colors.border}`,
                                borderRadius: 8,
                                minHeight: 40,
                              } as any}
                            />
                            <input
                              type="time"
                              value={slotStart.toTimeString().slice(0, 5)}
                              onChange={(e: any) => {
                                const [hours, minutes] = e.target.value.split(':');
                                const newDate = new Date(slotStart);
                                newDate.setHours(parseInt(hours), parseInt(minutes));
                                const updatedSlots = [...timeSlots];
                                updatedSlots[index] = {
                                  ...slot,
                                  startDate: newDate.toISOString()
                                };
                                updateNestedConfigField('calendar', 'timeSlots', updatedSlots);
                              }}
                              style={{
                                flex: 1,
                                padding: spacing.sm,
                                fontSize: typography.fontSize.base,
                                border: `1px solid ${colors.border}`,
                                borderRadius: 8,
                                minHeight: 40,
                              } as any}
                            />
                          </View>
                          <View style={{ flexDirection: 'row', gap: spacing.xs, marginBottom: spacing.xs }}>
                            <input
                              type="date"
                              value={slotEnd.toISOString().split('T')[0]}
                              onChange={(e: any) => {
                                const selectedDate = new Date(e.target.value);
                                selectedDate.setHours(slotEnd.getHours());
                                selectedDate.setMinutes(slotEnd.getMinutes());
                                const updatedSlots = [...timeSlots];
                                updatedSlots[index] = {
                                  ...slot,
                                  endDate: selectedDate.toISOString()
                                };
                                updateNestedConfigField('calendar', 'timeSlots', updatedSlots);
                              }}
                              style={{
                                flex: 1,
                                padding: spacing.sm,
                                fontSize: typography.fontSize.base,
                                border: `1px solid ${colors.border}`,
                                borderRadius: 8,
                                minHeight: 40,
                              } as any}
                            />
                            <input
                              type="time"
                              value={slotEnd.toTimeString().slice(0, 5)}
                              onChange={(e: any) => {
                                const [hours, minutes] = e.target.value.split(':');
                                const newDate = new Date(slotEnd);
                                newDate.setHours(parseInt(hours), parseInt(minutes));
                                const updatedSlots = [...timeSlots];
                                updatedSlots[index] = {
                                  ...slot,
                                  endDate: newDate.toISOString()
                                };
                                updateNestedConfigField('calendar', 'timeSlots', updatedSlots);
                              }}
                              style={{
                                flex: 1,
                                padding: spacing.sm,
                                fontSize: typography.fontSize.base,
                                border: `1px solid ${colors.border}`,
                                borderRadius: 8,
                                minHeight: 40,
                              } as any}
                            />
                          </View>
                        </>
                      ) : (
                        <Text style={styles.helperText}>
                          {formatDateTime(slotStart)} - {formatDateTime(slotEnd)}
                        </Text>
                      )}
                      <Input
                        label="Label (Optional)"
                        value={slot.label || ''}
                        onChangeText={(label) => {
                          const updatedSlots = [...timeSlots];
                          updatedSlots[index] = { ...slot, label };
                          updateNestedConfigField('calendar', 'timeSlots', updatedSlots);
                        }}
                        placeholder="e.g., Morning, Afternoon"
                      />
                    </View>
                  );
                })}
                {timeSlots.length === 0 && (
                  <Text style={styles.helperText}>No time slots added. You can add slots above or use a single start/end date below.</Text>
                )}
              </View>
            )}
            {calendarMode === 'propose' && (
              <>
                <View style={styles.field}>
                  <Text style={styles.label}>Start Date & Time {timeSlots.length === 0 ? '*' : '(Optional - if no slots)'}</Text>
                  {Platform.OS === 'web' ? (
                    <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                      <input
                        type="date"
                        value={startDate.toISOString().split('T')[0]}
                        onChange={(e: any) => {
                          const selectedDate = new Date(e.target.value);
                          selectedDate.setHours(startDate.getHours());
                          selectedDate.setMinutes(startDate.getMinutes());
                          updateNestedConfigField('calendar', 'startDate', selectedDate.toISOString());
                          // Auto-update end date if it's before start date
                          const currentEndDate = formData.config?.calendar?.endDate 
                            ? (typeof formData.config.calendar.endDate === 'string' 
                                ? new Date(formData.config.calendar.endDate) 
                                : formData.config.calendar.endDate)
                            : new Date(selectedDate.getTime() + 60 * 60 * 1000);
                          if (currentEndDate <= selectedDate) {
                            const newEndDate = new Date(selectedDate.getTime() + 60 * 60 * 1000);
                            updateNestedConfigField('calendar', 'endDate', newEndDate.toISOString());
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: spacing.sm,
                          fontSize: typography.fontSize.base,
                          border: `1px solid ${errors['config.calendar.startDate'] ? colors.error : colors.border}`,
                          borderRadius: 8,
                          minHeight: 48,
                        } as any}
                      />
                      <input
                        type="time"
                        value={startDate.toTimeString().slice(0, 5)}
                        onChange={(e: any) => {
                          const [hours, minutes] = e.target.value.split(':');
                          const newDate = new Date(startDate);
                          newDate.setHours(parseInt(hours), parseInt(minutes));
                          updateNestedConfigField('calendar', 'startDate', newDate.toISOString());
                        }}
                        style={{
                          flex: 1,
                          padding: spacing.sm,
                          fontSize: typography.fontSize.base,
                          border: `1px solid ${errors['config.calendar.startDate'] ? colors.error : colors.border}`,
                          borderRadius: 8,
                          minHeight: 48,
                        } as any}
                      />
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={[styles.dateTimeButton, errors['config.calendar.startDate'] && styles.dateTimeButtonError]}
                      onPress={() => {
                        // Native date picker will be implemented
                        setShowStartDatePicker(true);
                      }}
                    >
                      <Ionicons name="calendar-outline" size={20} color={colors.primary} />
                      <Text style={styles.dateTimeText}>
                        {formatDateTime(startDate) || 'Select start date'}
                      </Text>
                    </TouchableOpacity>
                  )}
                  {errors['config.calendar.startDate'] && (
                    <Text style={styles.errorText}>{errors['config.calendar.startDate']}</Text>
                  )}
                  {showStartDatePicker && Platform.OS !== 'web' && (
                    <Text style={styles.helperText}>
                      Native date/time picker will be implemented
                    </Text>
                  )}
                </View>
                <View style={styles.field}>
                  <Text style={styles.label}>End Date & Time</Text>
              {Platform.OS === 'web' ? (
                <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                  <input
                    type="date"
                    value={endDate.toISOString().split('T')[0]}
                    onChange={(e: any) => {
                      const selectedDate = new Date(e.target.value);
                      selectedDate.setHours(endDate.getHours());
                      selectedDate.setMinutes(endDate.getMinutes());
                      updateNestedConfigField('calendar', 'endDate', selectedDate.toISOString());
                    }}
                    style={{
                      flex: 1,
                      padding: spacing.sm,
                      fontSize: typography.fontSize.base,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 8,
                      minHeight: 48,
                    } as any}
                  />
                  <input
                    type="time"
                    value={endDate.toTimeString().slice(0, 5)}
                    onChange={(e: any) => {
                      const [hours, minutes] = e.target.value.split(':');
                      const newDate = new Date(endDate);
                      newDate.setHours(parseInt(hours), parseInt(minutes));
                      updateNestedConfigField('calendar', 'endDate', newDate.toISOString());
                    }}
                    style={{
                      flex: 1,
                      padding: spacing.sm,
                      fontSize: typography.fontSize.base,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 8,
                      minHeight: 48,
                    } as any}
                  />
                </View>
              ) : (
                <TouchableOpacity
                  style={styles.dateTimeButton}
                  onPress={() => {
                    // Native date picker will be implemented
                    setShowEndDatePicker(true);
                  }}
                >
                  <Ionicons name="calendar-outline" size={20} color={colors.primary} />
                  <Text style={styles.dateTimeText}>
                    {formatDateTime(endDate) || 'Select end date'}
                  </Text>
                </TouchableOpacity>
              )}
                  {showEndDatePicker && Platform.OS !== 'web' && (
                    <Text style={styles.helperText}>
                      Native date/time picker will be implemented
                    </Text>
                  )}
                </View>
              </>
            )}
            <View style={styles.field}>
              <Input
                label="Location"
                value={formData.config?.calendar?.location || ''}
                onChangeText={(location) => updateNestedConfigField('calendar', 'location', location)}
                placeholder="Enter event location"
              />
            </View>
            {calendarMode === 'pick' && (
              <View style={styles.field}>
                <Input
                  label="Duration (minutes)"
                  value={formData.config?.calendar?.durationMinutes?.toString() || '60'}
                  onChangeText={(duration) => {
                    const minutes = parseInt(duration, 10);
                    if (!isNaN(minutes) && minutes > 0) {
                      updateNestedConfigField('calendar', 'durationMinutes', minutes);
                    }
                  }}
                  placeholder="60"
                  keyboardType="numeric"
                />
                <Text style={styles.helperText}>Default duration for the appointment</Text>
              </View>
            )}
          </>
        );

      case 'assign_user':
        return (
          <>
            <View style={styles.field}>
              <View style={styles.sectionHeader}>
                <Text style={styles.label}>Users *</Text>
                <TouchableOpacity
                  style={styles.addUsersButton}
                  onPress={() => setShowUserSelector(true)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="person-add" size={18} color={colors.primary} />
                  <Text style={styles.addUsersButtonText}>Add Users</Text>
                </TouchableOpacity>
              </View>
              
              {selectedUsers.length === 0 ? (
                <View style={styles.emptyUsersContainer}>
                  <Ionicons name="people-outline" size={32} color={colors.text.secondary} />
                  <Text style={styles.emptyUsersText}>No users selected</Text>
                  <Text style={styles.emptyUsersSubtext}>Add users to assign conversations to them</Text>
                </View>
              ) : (
                <View style={styles.usersList}>
                  {selectedUsers.map((user) => (
                    <View key={user.userId} style={styles.userItem}>
                      <View style={styles.userItemInfo}>
                        <View style={styles.userItemAvatar}>
                          <Text style={styles.userItemAvatarText}>
                            {user.user?.firstName?.[0] || ''}{user.user?.lastName?.[0] || ''}
                          </Text>
                        </View>
                        <View style={styles.userItemDetails}>
                          <Text style={styles.userItemName}>
                            {user.user?.firstName || ''} {user.user?.lastName || ''}
                          </Text>
                          {user.user?.email ? (
                            <Text style={styles.userItemEmail}>{user.user.email}</Text>
                          ) : null}
                        </View>
                      </View>
                      <TouchableOpacity
                        style={styles.removeUserButton}
                        onPress={() => {
                          const currentUserIds = formData.config?.assignUser?.userIds || 
                                                (formData.config?.assignUser?.userId ? [formData.config.assignUser.userId] : []);
                          const updatedUserIds = currentUserIds.filter((id: string) => {
                            const idStr = String(id).trim();
                            const userUserIdStr = typeof user.userId === 'string' ? user.userId : 
                                                 (user.userId && typeof user.userId === 'object' && '_id' in user.userId ? 
                                                  String((user.userId as any)._id) : String(user.userId || ''));
                            return idStr !== userUserIdStr && idStr !== String(user.user?._id || '');
                          });
                          updateNestedConfigField('assignUser', 'userIds', updatedUserIds);
                          if (updatedUserIds.length > 0) {
                            updateNestedConfigField('assignUser', 'userId', updatedUserIds[0]); // Keep for backward compatibility
                          } else {
                            updateNestedConfigField('assignUser', 'userId', '');
                          }
                          setSelectedUsers(selectedUsers.filter(u => u.userId !== user.userId));
                        }}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Ionicons name="close-circle" size={20} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
              {(errors['config.assignUser.userIds'] || errors['config.assignUser.userId']) && (
                <Text style={styles.errorText}>{errors['config.assignUser.userIds'] || errors['config.assignUser.userId']}</Text>
              )}
            </View>
            
            <View style={styles.field}>
              <Input
                label="Button Text (Optional)"
                value={formData.config?.assignUser?.buttonText || ''}
                onChangeText={(buttonText) => updateNestedConfigField('assignUser', 'buttonText', buttonText)}
                placeholder="Assign to User"
              />
              <Text style={styles.helperText}>
                Text to display on the assignment button. If empty, the display text will be used.
              </Text>
            </View>
            
            <View style={styles.field}>
              <RichTextEditor
                label="Assignment Message (Optional)"
                value={formData.config?.assignUser?.message || ''}
                onChange={(message) => updateNestedConfigField('assignUser', 'message', message)}
                placeholder="Enter message to send after assignment"
                maxLength={4096}
              />
              <Text style={styles.helperText}>
                This message will be sent to the contact after the conversation is assigned to the selected user. (WhatsApp limit: 4096 characters)
              </Text>
            </View>
          </>
        );

      case 'assign_department':
        return (
          <>
            <View style={styles.field}>
              <View style={styles.sectionHeader}>
                <Text style={styles.label}>Departments *</Text>
                <TouchableOpacity
                  style={styles.addUsersButton}
                  onPress={() => setShowDepartmentSelector(true)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="business" size={18} color={colors.primary} />
                  <Text style={styles.addUsersButtonText}>Add Departments</Text>
                </TouchableOpacity>
              </View>
              
              {selectedDepartments.length === 0 ? (
                <View style={styles.emptyUsersContainer}>
                  <Ionicons name="business-outline" size={32} color={colors.text.secondary} />
                  <Text style={styles.emptyUsersText}>No departments selected</Text>
                  <Text style={styles.emptyUsersSubtext}>Add departments to assign conversations to them</Text>
                </View>
              ) : (
                <View style={styles.usersList}>
                  {selectedDepartments.map((dept) => (
                    <View key={dept._id} style={styles.userItem}>
                      <View style={styles.userItemInfo}>
                        <View style={[styles.userItemAvatar, styles.departmentAvatar]}>
                          <Ionicons name="business-outline" size={20} color={colors.primary} />
                        </View>
                        <View style={styles.userItemDetails}>
                          <Text style={styles.userItemName}>
                            {dept.name || 'Unknown Department'}
                          </Text>
                          {dept.description ? (
                            <Text style={styles.userItemEmail}>{dept.description}</Text>
                          ) : null}
                        </View>
                      </View>
                      <TouchableOpacity
                        style={styles.removeUserButton}
                        onPress={() => {
                          const currentDepartmentIds = formData.config?.assignDepartment?.departmentIds || 
                                                      (formData.config?.assignDepartment?.departmentId ? [formData.config.assignDepartment.departmentId] : []);
                          const updatedDepartmentIds = currentDepartmentIds.filter((id: string) => {
                            const idStr = String(id).trim();
                            const deptIdStr = String(dept._id || '').trim();
                            return idStr !== deptIdStr;
                          });
                          updateNestedConfigField('assignDepartment', 'departmentIds', updatedDepartmentIds);
                          if (updatedDepartmentIds.length > 0) {
                            updateNestedConfigField('assignDepartment', 'departmentId', updatedDepartmentIds[0]); // Keep for backward compatibility
                          } else {
                            updateNestedConfigField('assignDepartment', 'departmentId', '');
                          }
                          setSelectedDepartments(selectedDepartments.filter(d => d._id !== dept._id));
                        }}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Ionicons name="close-circle" size={20} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
              {(errors['config.assignDepartment.departmentIds'] || errors['config.assignDepartment.departmentId']) && (
                <Text style={styles.errorText}>{errors['config.assignDepartment.departmentIds'] || errors['config.assignDepartment.departmentId']}</Text>
              )}
            </View>
            
            <View style={styles.field}>
              <Input
                label="Button Text (Optional)"
                value={formData.config?.assignDepartment?.buttonText || ''}
                onChangeText={(buttonText) => updateNestedConfigField('assignDepartment', 'buttonText', buttonText)}
                placeholder="Assign to Department"
              />
              <Text style={styles.helperText}>
                Text to display on the assignment button. If empty, the display text will be used.
              </Text>
            </View>
            
            <View style={styles.field}>
              <RichTextEditor
                label="Assignment Message (Optional)"
                value={formData.config?.assignDepartment?.message || ''}
                onChange={(message) => updateNestedConfigField('assignDepartment', 'message', message)}
                placeholder="Enter message to send after assignment"
                maxLength={4096}
              />
              <Text style={styles.helperText}>
                This message will be sent to the contact after the conversation is assigned to the selected department. (WhatsApp limit: 4096 characters)
              </Text>
            </View>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.form}>
        {/* Display Text */}
        <View style={styles.field}>
          <Input
            label="Display Text *"
            value={formData.displayText}
            onChangeText={(text) => updateField('displayText', text)}
            placeholder="Text shown to contact (e.g., 'Get Help', 'View Products')"
            error={errors.displayText}
            maxLength={100}
          />
        </View>

        {/* Item Type */}
        <View style={styles.field}>
          <Text style={styles.label}>Item Type *</Text>
          <TouchableOpacity
            style={styles.typeSelector}
            onPress={() => setShowTypeSelector(true)}
          >
            <View style={styles.typeSelectorContent}>
              <Ionicons
                name={ITEM_TYPE_OPTIONS.find((o) => o.value === formData.type)?.icon as any}
                size={20}
                color={colors.primary}
              />
              <Text style={styles.typeSelectorText}>
                {ITEM_TYPE_OPTIONS.find((o) => o.value === formData.type)?.label}
              </Text>
            </View>
            <Ionicons name="chevron-down" size={20} color={colors.textLight} />
          </TouchableOpacity>
          <Text style={styles.helperText}>
            {ITEM_TYPE_OPTIONS.find((o) => o.value === formData.type)?.description}
          </Text>
        </View>

        {/* Type-specific fields */}
        {renderTypeSpecificFields()}

        {/* Skip Channel Webhooks (for non-webhook item types) */}
        {formData.type !== 'webhook' && (
          <View style={styles.field}>
            <Text style={styles.label}>Skip Channel Hooks</Text>
            <View style={styles.methodButtons}>
              {[
                { value: false, label: 'No' },
                { value: true, label: 'Yes' },
              ].map((option) => (
                <TouchableOpacity
                  key={option.label}
                  style={[
                    styles.methodButton,
                    !!formData.config?.skipChannelHooks === option.value && styles.methodButtonActive,
                  ]}
                  onPress={() => updateConfigField('skipChannelHooks', option.value)}
                >
                  <Text
                    style={[
                      styles.methodButtonText,
                      !!formData.config?.skipChannelHooks === option.value && styles.methodButtonTextActive,
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={styles.helperText}>
              When enabled, the regular inbound channel webhook will be skipped for this click.
            </Text>
          </View>
        )}

        {/* Sets (for linking item to sets) */}
        <View style={styles.field}>
          <Text style={styles.label}>Link to Sets (optional)</Text>
          <TouchableOpacity
            style={styles.selectButton}
            onPress={() => setShowSetsSelector(true)}
          >
            <View style={styles.selectButtonContent}>
              {selectedSetIds.length > 0 ? (
                <Text style={styles.selectButtonText}>
                  {selectedSetIds.length} set{selectedSetIds.length !== 1 ? 's' : ''} selected
                </Text>
              ) : (
                <Text style={[styles.selectButtonText, styles.selectButtonPlaceholder]}>
                  Select sets to link this item to
                </Text>
              )}
            </View>
            <Ionicons name="chevron-down" size={20} color={colors.textLight} />
          </TouchableOpacity>
          <Text style={styles.helperText}>
            Link this item to one or more reply sets
          </Text>
        </View>

        {/* Icon (optional) */}
        <View style={styles.field}>
          <Input
            label="Icon (optional)"
            value={formData.icon || ''}
            onChangeText={(icon) => updateField('icon', icon)}
            placeholder="Emoji or icon identifier"
            helperText="Add an emoji or icon to display with this item"
          />
        </View>

        {/* Actions */}
        {!hideActions && (
          <View style={styles.actions}>
            {onCancel && (
              <Button
                title="Cancel"
                onPress={onCancel}
                variant="outline"
                style={styles.cancelButton}
                disabled={loading}
              />
            )}
            <Button
              title={submitLabel}
              onPress={handleSubmit}
              variant="primary"
              loading={loading}
              disabled={loading}
              style={styles.submitButton}
            />
          </View>
        )}
      </View>

      {/* Type Selector Modal */}
      <Modal
        visible={showTypeSelector}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowTypeSelector(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Item Type</Text>
              <TouchableOpacity onPress={() => setShowTypeSelector(false)}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={ITEM_TYPE_OPTIONS}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => {
                const isDisabled = item.disabled === true;
                return (
                  <TouchableOpacity
                    style={[
                      styles.typeOption,
                      formData.type === item.value && styles.typeOptionSelected,
                      isDisabled && styles.typeOptionDisabled,
                    ]}
                    onPress={() => {
                      if (!isDisabled) {
                        updateField('type', item.value);
                        // Reset config when type changes
                        setFormData((prev) => ({ ...prev, config: {} }));
                        setShowTypeSelector(false);
                      }
                    }}
                    disabled={isDisabled}
                    activeOpacity={isDisabled ? 1 : 0.7}
                  >
                    <Ionicons 
                      name={item.icon as any} 
                      size={24} 
                      color={isDisabled ? colors.textLight : colors.primary} 
                    />
                    <View style={styles.typeOptionContent}>
                      <Text style={[
                        styles.typeOptionLabel,
                        isDisabled && styles.typeOptionLabelDisabled
                      ]}>
                        {item.label}
                        {isDisabled && ' (Coming Soon)'}
                      </Text>
                      <Text style={[
                        styles.typeOptionDescription,
                        isDisabled && styles.typeOptionDescriptionDisabled
                      ]}>
                        {item.description}
                      </Text>
                    </View>
                    {formData.type === item.value && !isDisabled && (
                      <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        </View>
      </Modal>

      {/* Sets Selector Modal */}
      <Modal
        visible={showSetsSelector}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowSetsSelector(false);
          setSetsSearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Sets</Text>
              <TouchableOpacity
                onPress={() => {
                  setShowSetsSelector(false);
                  setSetsSearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search sets..."
                value={setsSearchQuery}
                onChangeText={setSetsSearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {setsSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setSetsSearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {/* Sets List */}
            <FlatList
              data={filteredSets}
              keyExtractor={(item) => item._id}
              renderItem={({ item }) => {
                const isSelected = selectedSetIds.includes(item._id);
                return (
                  <TouchableOpacity
                    style={[
                      styles.typeOption,
                      isSelected && styles.typeOptionSelected,
                    ]}
                    onPress={() => handleSetToggle(item._id)}
                  >
                    <Ionicons name="layers-outline" size={24} color={colors.primary} />
                    <View style={styles.typeOptionContent}>
                      <Text style={styles.typeOptionLabel}>{item.name}</Text>
                    </View>
                    {isSelected && (
                      <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                );
              }}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>
                    {availableRepliesSets.length === 0
                      ? 'No sets available'
                      : 'No sets found'}
                  </Text>
                </View>
              }
            />
          </View>
        </View>
      </Modal>

      {/* Catalog Selector Modal */}
      <Modal
        visible={showCatalogSelector}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowCatalogSelector(false);
          setCatalogSearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Catalog</Text>
              <TouchableOpacity
                onPress={() => {
                  setShowCatalogSelector(false);
                  setCatalogSearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search catalogues..."
                value={catalogSearchQuery}
                onChangeText={setCatalogSearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {catalogSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setCatalogSearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {/* Catalogues List */}
            {loadingCatalogues ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading catalogues...</Text>
              </View>
            ) : (
              <FlatList
                data={filteredCatalogues}
                keyExtractor={(item) => item._id}
                renderItem={({ item }) => {
                  const isSelected = formData.config?.catalogId === item._id;
                  return (
                    <TouchableOpacity
                      style={[
                        styles.typeOption,
                        isSelected && styles.typeOptionSelected,
                      ]}
                      onPress={() => handleCatalogSelect(item._id)}
                    >
                      <Ionicons name="grid-outline" size={24} color={colors.primary} />
                      <View style={styles.typeOptionContent}>
                        <Text style={styles.typeOptionLabel}>{item.name}</Text>
                      </View>
                      {isSelected && (
                        <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                      )}
                    </TouchableOpacity>
                  );
                }}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      {catalogues.length === 0
                        ? 'No catalogues available'
                        : 'No catalogues found'}
                    </Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Replies Set Selector Modal */}
      <Modal
        visible={showRepliesSetSelector}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowRepliesSetSelector(false);
          setRepliesSetSearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Replies Set</Text>
              <TouchableOpacity
                onPress={() => {
                  setShowRepliesSetSelector(false);
                  setRepliesSetSearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search sets..."
                value={repliesSetSearchQuery}
                onChangeText={setRepliesSetSearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {repliesSetSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setRepliesSetSearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {/* Replies Sets List */}
            {loadingSets ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading sets...</Text>
              </View>
            ) : (
              <FlatList
                data={filteredRepliesSets}
                keyExtractor={(item) => item._id}
                renderItem={({ item }) => {
                  const isSelected = formData.config?.repliesSetId === item._id;
                  return (
                    <TouchableOpacity
                      style={[
                        styles.typeOption,
                        isSelected && styles.typeOptionSelected,
                      ]}
                      onPress={() => handleRepliesSetSelect(item._id)}
                    >
                      <Ionicons name="layers-outline" size={24} color={colors.primary} />
                      <View style={styles.typeOptionContent}>
                        <Text style={styles.typeOptionLabel}>{item.name}</Text>
                      </View>
                      {isSelected && (
                        <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                      )}
                    </TouchableOpacity>
                  );
                }}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      {allAvailableSets.length === 0
                        ? 'No sets available'
                        : 'No sets found'}
                    </Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Product Selector Modal */}
      <Modal
        visible={showProductSelector}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowProductSelector(false);
          setProductSearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Product</Text>
              <TouchableOpacity
                onPress={() => {
                  setShowProductSelector(false);
                  setProductSearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search products..."
                value={productSearchQuery}
                onChangeText={setProductSearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {productSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setProductSearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {loadingProducts ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading products...</Text>
              </View>
            ) : (
              <FlatList
                data={filteredProducts}
                keyExtractor={(item) => item._id}
                renderItem={({ item }) => {
                  const isSelected = formData.config?.productId === item._id;
                  return (
                    <TouchableOpacity
                      style={[
                        styles.typeOption,
                        isSelected && styles.typeOptionSelected,
                      ]}
                      onPress={() => handleProductSelect(item._id)}
                    >
                      <Ionicons name="cube-outline" size={24} color={colors.primary} />
                      <View style={styles.typeOptionContent}>
                        <Text style={styles.typeOptionLabel}>{item.name}</Text>
                        {item.catalogueName && (
                          <Text style={styles.typeOptionSubLabel}>From: {item.catalogueName}</Text>
                        )}
                      </View>
                      {isSelected && (
                        <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                      )}
                    </TouchableOpacity>
                  );
                }}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      {products.length === 0
                        ? 'No products available'
                        : 'No products found'}
                    </Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Calendar Selector Modal */}
      <Modal
        visible={showCalendarSelector}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowCalendarSelector(false);
          setCalendarSearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Calendar</Text>
              <TouchableOpacity
                onPress={() => {
                  setShowCalendarSelector(false);
                  setCalendarSearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search calendars..."
                value={calendarSearchQuery}
                onChangeText={setCalendarSearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {calendarSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setCalendarSearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {/* Calendars List */}
            {loadingCalendars ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading calendars...</Text>
              </View>
            ) : (
              <FlatList
                data={filteredCalendars}
                keyExtractor={(item) => item._id}
                renderItem={({ item }) => {
                  const isSelected = formData.config?.calendar?.calendarId === item._id;
                  return (
                    <TouchableOpacity
                      style={[
                        styles.typeOption,
                        isSelected && styles.typeOptionSelected,
                      ]}
                      onPress={() => handleCalendarSelect(item._id)}
                    >
                      <Ionicons name="calendar-outline" size={24} color={colors.primary} />
                      <View style={styles.typeOptionContent}>
                        <Text style={styles.typeOptionLabel}>{item.name}</Text>
                      </View>
                      {isSelected && (
                        <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                      )}
                    </TouchableOpacity>
                  );
                }}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      {calendars.length === 0
                        ? 'No calendars available'
                        : 'No calendars found'}
                    </Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Business User Picker for Assign User */}
      <BusinessUserPicker
        visible={showUserSelector}
        onClose={() => setShowUserSelector(false)}
        onConfirm={handleUserSelect}
        mode="multiple"
        title="Select Users"
        confirmText="Add Users"
        selectedUserIds={formData.config?.assignUser?.userIds || 
                        (formData.config?.assignUser?.userId ? [formData.config.assignUser.userId] : [])}
        businessId={currentBusiness?._id}
      />
      
      {/* Business User Picker for Assign Department */}
      <BusinessUserPicker
        visible={showDepartmentSelector}
        onClose={() => setShowDepartmentSelector(false)}
        onConfirm={handleDepartmentSelect}
        mode="multiple"
        source="departments"
        title="Select Departments"
        confirmText="Add Departments"
        selectedUserIds={formData.config?.assignDepartment?.departmentIds || 
                        (formData.config?.assignDepartment?.departmentId ? [formData.config.assignDepartment.departmentId] : [])}
        businessId={currentBusiness?._id}
      />
      
      {/* Location Picker for Fixed Message */}
      <MapLocationPicker
        visible={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={(location) => {
          updateNestedConfigField('fixedMessage', 'latitude', location.latitude);
          updateNestedConfigField('fixedMessage', 'longitude', location.longitude);
          updateNestedConfigField('fixedMessage', 'address', location.address);
          setShowLocationPicker(false);
        }}
        initialLocation={
          formData.config?.fixedMessage?.latitude && formData.config?.fixedMessage?.longitude
            ? {
                latitude: formData.config.fixedMessage.latitude,
                longitude: formData.config.fixedMessage.longitude,
                address: formData.config.fixedMessage.address || '',
              }
            : null
        }
      />

      {/* Voice Recorder Modal for Fixed Message */}
      <Modal
        visible={showVoiceRecorder}
        transparent={true}
        animationType="slide"
        onRequestClose={handleVoiceRecordingCancel}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={handleVoiceRecordingCancel}
        >
          <VoiceRecorder
            onRecordingComplete={handleVoiceRecordingComplete}
            onRecordingCancel={handleVoiceRecordingCancel}
            maxDuration={120}
          />
        </TouchableOpacity>
      </Modal>

      {/* Item Selector Modal for Fixed Message */}
      <Modal
        visible={showItemSelector}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          setShowItemSelector(false);
          setItemSearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Items (Max 3)</Text>
              <TouchableOpacity
                onPress={() => {
                  setShowItemSelector(false);
                  setItemSearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search items..."
                value={itemSearchQuery}
                onChangeText={setItemSearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {itemSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setItemSearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {/* Items List */}
            {loadingItems ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading items...</Text>
              </View>
            ) : (
              <FlatList
                data={filteredItems}
                keyExtractor={(item) => item._id}
                renderItem={({ item }) => {
                  const isSelected = (formData.config?.fixedMessage?.selectedItems || []).includes(item._id);
                  const isDisabled = isSelected || (formData.config?.fixedMessage?.selectedItems || []).length >= 3;
                  return (
                    <TouchableOpacity
                      style={[
                        styles.typeOption,
                        isSelected && styles.typeOptionSelected,
                        isDisabled && !isSelected && styles.typeOptionDisabled,
                      ]}
                      onPress={() => {
                        if (!isDisabled) {
                          handleItemSelect(item._id);
                        }
                      }}
                      disabled={isDisabled}
                    >
                      {item.icon ? (
                        <Text style={styles.userItemAvatarText}>{item.icon}</Text>
                      ) : (
                        <Ionicons name="chatbubble-outline" size={24} color={colors.primary} />
                      )}
                      <View style={styles.typeOptionContent}>
                        <Text style={styles.typeOptionLabel}>{item.displayText}</Text>
                        <Text style={styles.typeOptionDescription}>{item.type}</Text>
                      </View>
                      {isSelected && (
                        <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                      )}
                    </TouchableOpacity>
                  );
                }}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      {availableItems.length === 0
                        ? 'No items available'
                        : 'No items found'}
                    </Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
  },
  form: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    ...designTokens.shadows.sm,
  },
  field: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs,
  },
  helperText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs / 2,
  },
  errorText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs / 2,
  },
  typeSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surfaceRaised,
  },
  typeSelectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  typeSelectorText: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surfaceRaised,
  },
  selectButtonError: {
    borderColor: colors.error,
  },
  selectButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
  },
  selectButtonPlaceholder: {
    color: colors.textLight,
  },
  selectButtonContent: {
    flex: 1,
  },
  dateTimeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surfaceRaised,
  },
  dateTimeButtonError: {
    borderColor: colors.error,
  },
  dateTimeText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  searchIcon: {
    marginRight: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
    paddingVertical: spacing.xs,
  },
  clearSearchButton: {
    marginLeft: spacing.sm,
    padding: spacing.xs / 2,
  },
  emptyContainer: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  methodButtons: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  methodButton: {
    flex: 1,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surfaceRaised,
    alignItems: 'center',
  },
  methodButtonActive: {
    borderColor: colors.primary,
    backgroundColor: `${String(colors.primary)}15`,
  },
  methodButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  webhookOptionSubText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs / 2,
  },
  methodButtonTextActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
    ...(Platform.OS === 'web' && Dimensions.get('window').width >= 768 && {
      position: 'fixed' as any,
      top: 180, // Header height (matches webContentArea paddingTop)
      bottom: 0,
      left: 355, // Left pane width
      right: 355, // Right pane width
      width: 'auto' as any,
    }),
  },
  modalContent: {
    backgroundColor: colors.background.base,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    maxHeight: '80%',
    paddingBottom: spacing.lg,
    ...(Platform.OS === 'web' && Dimensions.get('window').width >= 768 && {
      maxWidth: '100%',
      width: '100%',
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  typeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: `${String(colors.border)}40`,
    gap: spacing.md,
  },
  typeOptionSelected: {
    backgroundColor: `${String(colors.primary)}10`,
  },
  typeOptionDisabled: {
    opacity: 0.5,
    backgroundColor: colors.lightGray,
  },
  typeOptionContent: {
    flex: 1,
  },
  typeOptionLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  typeOptionLabelDisabled: {
    color: colors.textLight,
  },
  typeOptionSubLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs / 2,
  },
  typeOptionDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  typeOptionDescriptionDisabled: {
    opacity: 0.7,
  },
  section: {
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    marginBottom: spacing.md,
  },
  languageButtons: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  languageButton: {
    flex: 1,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surfaceRaised,
    alignItems: 'center',
  },
  languageButtonActive: {
    borderColor: colors.primary,
    backgroundColor: `${String(colors.primary)}15`,
  },
  languageButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  languageButtonTextActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  selectedManagerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  managerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  managerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: withAlpha(colors.primary, 0.13),
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  managerAvatarText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  managerDetails: {
    flex: 1,
  },
  managerName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  managerEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  removeManagerButton: {
    padding: spacing.xs,
  },
  selectManagerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  selectManagerButtonText: {
    marginLeft: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  addUsersButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    backgroundColor: withAlpha(colors.primary, 0.06),
    borderRadius: designTokens.borderRadius.sm,
    gap: spacing.xs,
  },
  addUsersButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  emptyUsersContainer: {
    alignItems: 'center',
    padding: spacing.lg,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  emptyUsersText: {
    marginTop: spacing.sm,
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  emptyUsersSubtext: {
    marginTop: spacing.xs,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    textAlign: 'center',
  },
  usersList: {
    gap: spacing.sm,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  userItemInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  userItemAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: withAlpha(colors.primary, 0.13),
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  departmentAvatar: {
    backgroundColor: withAlpha(colors.primary, 0.13),
  },
  userItemAvatarText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  userItemDetails: {
    flex: 1,
  },
  userItemName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  userItemEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  removeUserButton: {
    padding: spacing.xs,
  },
  messageTypeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.xs,
    justifyContent: 'space-between',
  },
  messageTypeOption: {
    width: Platform.OS === 'web' ? '32%' : '48%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
  },
  messageTypeOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  messageTypeLabel: {
    marginTop: spacing.xs,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
    textAlign: 'center',
  },
  messageTypeLabelSelected: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  selectedMediaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
    padding: spacing.sm,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    gap: spacing.sm,
  },
  selectedMediaText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
});

